import { css, html, LitElement, nothing } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import {
    TeqtankCommerce,
    type Commerce,
    type CommerceAddress,
    type CommerceAddressRegion,
    type CommerceCheckout,
    type CommerceCheckoutPaymentConfiguration,
    type CommerceDataSource,
    type CommerceEventUnsubscribe,
    type CommerceCustomerIdentityField,
    type CommerceCustomerRegistrationConfiguration,
    type CommerceCustomerSponsor,
    type CommerceEnrollmentSession,
    type CommercePaymentResult,
    type CommerceRecurringFrequencyOption,
} from "@teqtank/commerce-sdk";
import "./teqtank-checkout-summary.js";
import { TeqtankPayment } from "../payment/teqtank-payment.js";
import "../share/teqtank-share-panel.js";
import "../customer/teqtank-login.js";
import "../orders/teqtank-order-complete.js";
import { sanitizeCommerceHtml } from "../products/sanitize-commerce-html.js";
import {
    enrollmentStageRoutesConverter,
    isCurrentBrowserRoute,
    resolveEnrollmentStageRoute,
    type EnrollmentStageRoutes,
} from "../enrollment/enrollment-stage-routes.js";

const calculationFieldNames = [
    "email",
    "phone",
    "firstName",
    "lastName",
    "addressLine1",
    "addressLine2",
    "city",
    "region",
    "postalCode",
    "countryCode",
] as const;

const unknownCalculationFailureMessage =
    "We couldn't calculate your order. Please review your details and try again. If the problem continues, contact support.";

type CalculationFieldName = (typeof calculationFieldNames)[number];
type CalculationFieldValues = Readonly<Record<CalculationFieldName, string>>;

@customElement("teqtank-checkout")
export class TeqtankCheckout extends LitElement {
    @property({ attribute: false }) public accessor commerce: Commerce | undefined;
    @property({ attribute: "commerce-key" }) public accessor commerceKey = "demo";
    @property({ attribute: "data-source" })
    public accessor dataSource: CommerceDataSource = "playground";
    @property({ attribute: "shop-route" }) public accessor shopRoute = "/shop";
    @property({ attribute: "enrollment-route" }) public accessor enrollmentRoute = "/enrollment";
    @property({ attribute: "post-checkout-enrollment-route" })
    public accessor postCheckoutEnrollmentRoute = "";
    @property({ attribute: "enrollment-stage-routes", converter: enrollmentStageRoutesConverter })
    public accessor enrollmentStageRoutes: EnrollmentStageRoutes = Object.freeze({});
    @property({ attribute: "success-route" }) public accessor successRoute = "/order/complete";
    @property({ attribute: false })
    public accessor paymentFrameOrigins: string | readonly string[] | undefined;
    @property({ attribute: "payment-redirect-loader" })
    public accessor paymentRedirectLoader = "https://tts.teqtank.com/paymentloader";
    @property({ attribute: false }) public accessor paymentStyles:
        Readonly<Record<string, unknown>> | undefined;
    @property({ attribute: "account-password" })
    public accessor accountPassword: "optional" | "required" = "optional";
    @property({ attribute: false })
    public accessor identityValidationFields: readonly CommerceCustomerIdentityField[] = [
        "email",
        "phone",
    ];
    @property({ attribute: "mock-payment", type: Boolean })
    public accessor mockPayment = false;
    @property({ attribute: "show-share-cart", type: Boolean })
    public accessor showShareCart = true;
    @property({ attribute: "hide-share-cart", type: Boolean })
    public accessor hideShareCart = false;
    @query("teqtank-payment")
    private accessor paymentElement: TeqtankPayment | null = null;
    @query("dialog.share-cart-panel")
    private accessor shareCartDialog: HTMLDialogElement | null = null;
    @state() private accessor checkout: CommerceCheckout | undefined;
    @state() private accessor paymentConfiguration:
        CommerceCheckoutPaymentConfiguration | undefined;
    @state() private accessor loading = true;
    @state() private accessor saving = false;
    @state() private accessor paymentProcessing = false;
    @state() private accessor errorMessage = "";
    @state() private accessor calculationErrorMessage = "";
    @state() private accessor submissionErrorMessage = "";
    @state() private accessor step: "information" | "payment" | "complete" = "information";
    @state() private accessor completedOrderId = "";
    @state() private accessor enrollmentContinuation: CommerceEnrollmentSession | undefined;
    @state() private accessor accountApplyPrompt = false;
    @state() private accessor identityAuthenticationRequired = false;
    @state() private accessor newAccountRequired = false;
    @state() private accessor newAccountPassword = "";
    @state() private accessor registration: CommerceCustomerRegistrationConfiguration | undefined;
    @state() private accessor sponsorWebAlias = "";
    @state() private accessor noSponsor = false;
    @state() private accessor sponsorResults: readonly CommerceCustomerSponsor[] = [];
    @state() private accessor humanVerificationToken = "";
    @state() private accessor humanVerificationError = "";
    @state() private accessor billingSameAsShipping = false;
    @state() private accessor consentSelections: Readonly<Record<string, boolean>> = {};
    @state() private accessor customFieldValues: Readonly<Record<string, string>> = {};
    @state() private accessor newAddressSelected = false;
    @state() private accessor editableContact:
        { email: string; phone: string; acceptsMarketing: boolean } | undefined;
    @state() private accessor editableAddress: CommerceAddress | undefined;
    @state() private accessor shareCartOpen = false;
    @state() private accessor calculationStale = true;
    @state() private accessor calculating = false;
    @state() private accessor addressRegions: readonly CommerceAddressRegion[] = [];
    @state() private accessor addressRegionsLoading = false;
    @state() private accessor addressRegionsErrorMessage = "";
    @state() private accessor recurringSavings: Readonly<
        Record<
            string,
            {
                saving?: string;
                oneTimePrice: string;
                recurringPrice: string;
                frequencyOptions: readonly CommerceRecurringFrequencyOption[];
                defaultFrequencyId: string;
                defaultFrequencyDays: number;
            }
        >
    > = {};
    private recurringSavingsLoadId = 0;
    private informationDirty = false;
    private ownsCommerce = false;
    private calculationTimer: ReturnType<typeof setTimeout> | undefined;
    private calculationInFlight = false;
    private calculationPending = false;
    private readonly calculationIdleWaiters: Array<() => void> = [];
    private informationRevision = 0;
    private lastAutomaticCalculationRevision = -1;
    private informationValueCheckTimer: ReturnType<typeof setInterval> | undefined;
    private observedCalculationValues: CalculationFieldValues | undefined;
    private unsubscribeMarket: CommerceEventUnsubscribe | undefined;
    private unsubscribeCheckout: CommerceEventUnsubscribe | undefined;
    private unsubscribeCart: CommerceEventUnsubscribe | undefined;
    private externalCheckoutRevision = 0;
    private sponsorSearchTimer: ReturnType<typeof setTimeout> | undefined;
    private recaptchaWidgetId: number | undefined;
    private recaptchaContainer: HTMLElement | undefined;
    private recaptchaInitializationFailed = false;
    private recaptchaRecoveryAttempts = 0;

    public static override styles = css`
        :host {
            --tt-ink: var(--teqtank-color-text, #16221d);
            --tt-muted: var(--teqtank-color-text-muted, #6b746e);
            --tt-accent: var(--teqtank-color-accent, #728f4f);
            --tt-surface: var(--teqtank-color-surface, #faf8f4);
            --tt-soft: var(--teqtank-color-surface-soft, #eee8df);
            --tt-line: var(--teqtank-color-border, rgba(22, 34, 29, 0.12));
            --tt-display: var(--teqtank-font-heading, Georgia, serif);
            --tt-body: var(--teqtank-font-body, Inter, ui-sans-serif, system-ui, sans-serif);
            --tt-field-background: var(--teqtank-checkout-field-background, #fff);
            --tt-field-radius: var(
                --teqtank-checkout-field-radius,
                var(--teqtank-radius-small, 10px)
            );
            --tt-option-background: var(--teqtank-checkout-option-background, #fff);
            display: block;
            container-type: inline-size;
            container-name: checkout;
            scroll-margin-block-start: var(--teqtank-checkout-scroll-offset, 6rem);
            color: var(--tt-ink);
            font-family: var(--tt-body);
        }

        .shell {
            padding: clamp(2rem, 5cqw, 5rem) max(5cqw, 24px);
            display: grid;
            grid-template-columns: var(--teqtank-checkout-columns, minmax(0, 1fr) 430px);
            gap: var(--teqtank-checkout-gap, 6cqw);
            align-items: start;
            background: var(--teqtank-color-background, var(--tt-surface));
        }

        main {
            min-width: 0;
            max-width: 760px;
        }

        .back {
            display: inline-flex;
            margin-bottom: 1.25rem;
            color: inherit;
            font-size: 12px;
            text-decoration: none;
            border: 0;
            padding: 0;
            background: none;
            cursor: pointer;
        }

        .eyebrow {
            margin: 0;
            color: var(--tt-accent);
            font-size: 10px;
            font-weight: 800;
            letter-spacing: 0.12em;
        }

        h1 {
            margin: 0.45rem 0 0;
            font: 500 clamp(2.5rem, 5cqw, 52px)/1 var(--tt-display);
            letter-spacing: -0.035em;
        }

        .steps {
            margin: 25px 0 45px;
            display: flex;
            gap: 8px;
            flex-wrap: wrap;
            font-size: 11px;
        }

        .steps span {
            padding: 7px 11px;
            border: 1px solid transparent;
            border-radius: var(--teqtank-radius-pill, 999px);
            background: color-mix(in srgb, var(--tt-soft) 68%, transparent);
            opacity: 0.62;
        }

        .steps .active {
            border-color: color-mix(in srgb, var(--tt-accent) 34%, var(--tt-line));
            background: color-mix(in srgb, var(--tt-accent) 10%, #fff);
            opacity: 1;
            font-weight: 700;
        }

        .form {
            display: block;
        }

        .section {
            margin-bottom: clamp(34px, 5cqw, 52px);
            padding: 0 0 clamp(34px, 5cqw, 52px);
            border: 0;
            border-bottom: 1px solid var(--tt-line);
            border-radius: var(--teqtank-checkout-section-radius, 0);
            background: var(--teqtank-checkout-section-background, transparent);
            box-shadow: var(--teqtank-checkout-section-shadow, none);
        }

        .payment-section {
            margin-bottom: 0;
            border-bottom: 0;
        }

        .title {
            margin-bottom: 1.25rem;
            display: flex;
            gap: 20px;
            align-items: baseline;
        }

        .title span {
            width: 20px;
            display: inline-block;
            flex: 0 0 auto;
            color: var(--tt-accent);
            font-size: 10px;
            font-weight: 700;
            letter-spacing: 0.08em;
        }

        .title h2 {
            margin: 0;
            font: 500 clamp(27px, 4cqw, 34px) var(--tt-display);
            letter-spacing: -0.02em;
        }

        .grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 15px;
        }

        .wide {
            grid-column: 1 / -1;
        }

        label {
            display: flex;
            flex-direction: column;
            gap: 6px;
            color: inherit;
            font-size: 11px;
            font-weight: 500;
        }

        input,
        select {
            width: 100%;
            min-height: var(--teqtank-control-height, 46px);
            box-sizing: border-box;
            padding: 12px 14px;
            border: 1px solid var(--tt-line);
            border-radius: var(--tt-field-radius);
            background: var(--tt-field-background);
            color: var(--tt-ink);
            font: inherit;
            box-shadow: inset 0 1px 2px color-mix(in srgb, var(--tt-ink) 4%, transparent);
            transition:
                border-color var(--teqtank-motion-fast, 120ms) ease,
                box-shadow var(--teqtank-motion-fast, 120ms) ease,
                background var(--teqtank-motion-fast, 120ms) ease;
        }

        input:focus,
        select:focus {
            outline: none;
            border-color: var(--tt-accent);
            background: #fff;
            box-shadow:
                0 0 0 3px color-mix(in srgb, var(--tt-accent) 17%, transparent),
                inset 0 1px 2px color-mix(in srgb, var(--tt-ink) 3%, transparent);
        }

        .checkbox {
            flex-direction: row;
            align-items: center;
            gap: 0.55rem;
        }

        .checkbox input,
        .method input {
            width: auto;
            min-height: 0;
            accent-color: var(--tt-accent);
        }

        .delivery {
            display: grid;
            gap: 8px;
        }

        .method {
            display: grid;
            grid-template-columns: auto 1fr auto;
            align-items: center;
            gap: 13px;
            padding: 17px 18px;
            border: 1px solid var(--tt-line);
            border-radius: var(--teqtank-checkout-option-radius, var(--tt-field-radius));
            background: var(--tt-option-background);
            cursor: pointer;
            transition:
                transform var(--teqtank-motion-fast, 120ms) ease,
                border-color var(--teqtank-motion-fast, 120ms) ease,
                box-shadow var(--teqtank-motion-fast, 120ms) ease;
        }

        .method:hover {
            transform: translateY(-1px);
            border-color: color-mix(in srgb, var(--tt-accent) 45%, var(--tt-line));
            box-shadow: 0 8px 22px color-mix(in srgb, var(--tt-ink) 6%, transparent);
        }

        .method:has(input:checked) {
            border-color: var(--tt-accent);
            background: color-mix(in srgb, var(--tt-accent) 7%, #fff);
            box-shadow:
                inset 0 0 0 1px var(--tt-accent),
                0 10px 26px color-mix(in srgb, var(--tt-accent) 10%, transparent);
        }

        .method span,
        .method strong,
        .method small {
            display: block;
        }

        .method small {
            margin-top: 2px;
            color: var(--tt-muted);
        }

        .saved-addresses {
            display: grid;
            gap: 8px;
            margin-bottom: 18px;
        }

        .saved-address {
            display: grid;
            grid-template-columns: auto 1fr;
            gap: 13px;
            padding: 16px;
            border: 1px solid var(--tt-line);
            border-radius: var(--teqtank-checkout-option-radius, var(--tt-field-radius));
            background: var(--tt-option-background);
            cursor: pointer;
            transition:
                transform 180ms ease,
                border-color 180ms ease,
                box-shadow 180ms ease,
                background 180ms ease;
        }

        .saved-address:hover {
            transform: translateY(-1px);
            border-color: color-mix(in srgb, var(--tt-accent) 45%, var(--tt-line));
            box-shadow: 0 8px 24px rgba(22, 34, 29, 0.07);
        }

        .saved-address:has(input:checked) {
            border-color: var(--tt-accent);
            box-shadow: inset 0 0 0 1px var(--tt-accent);
        }

        .saved-address input {
            width: auto;
        }
        .saved-address strong,
        .saved-address small {
            display: block;
        }
        .saved-address small {
            margin-top: 3px;
            color: var(--tt-muted);
        }

        .recipient-grid {
            margin-top: 18px;
        }

        .address-detail-fields {
            margin-top: 15px;
            animation: checkout-address-reveal 220ms ease-out both;
        }

        .address-validation {
            margin-top: 18px;
            padding: 18px;
            border: 1px solid color-mix(in srgb, var(--tt-accent) 45%, var(--tt-line));
            border-radius: 12px;
            background: color-mix(in srgb, var(--tt-accent) 7%, #fff);
        }
        .address-validation h3 {
            margin: 0 0 8px;
            font: 600 18px var(--tt-display);
        }
        .address-validation p {
            margin: 0 0 12px;
            color: var(--tt-muted);
            font-size: 12px;
            line-height: 1.5;
        }
        .address-validation address {
            margin: 0 0 16px;
            font-style: normal;
            line-height: 1.5;
        }
        .address-validation-actions {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
        }
        .address-validation-actions button {
            min-height: 42px;
            padding: 10px 14px;
        }

        @keyframes checkout-address-reveal {
            from {
                opacity: 0;
                transform: translateY(-6px);
            }
            to {
                opacity: 1;
                transform: translateY(0);
            }
        }

        @media (prefers-reduced-motion: reduce) {
            .saved-address,
            .address-detail-fields,
            .checkout-upsell-card {
                transition: none;
                animation: none;
            }
        }

        .account-gate {
            margin: 20px 0;
            padding: 20px;
            border: 1px solid var(--tt-line);
            border-radius: var(--teqtank-checkout-option-radius, var(--tt-field-radius));
            background: color-mix(in srgb, var(--tt-soft) 45%, #fff);
        }

        .account-gate h3 {
            margin: 0 0 8px;
            font: 500 22px var(--tt-display);
        }
        .account-gate p {
            margin: 0 0 16px;
            color: var(--tt-muted);
        }
        .registration-options {
            display: grid;
            grid-template-columns: minmax(0, 1fr);
            gap: 14px;
            margin-top: 18px;
        }
        .registration-options .checkbox {
            align-items: flex-start;
        }
        .registration-options small {
            display: block;
            margin-top: 4px;
            color: var(--tt-muted);
            line-height: 1.45;
        }
        .registration-options .checkbox a {
            color: inherit;
            text-decoration: underline;
            text-decoration-thickness: 1px;
            text-underline-offset: 0.16em;
        }
        .registration-options .checkbox a:hover {
            color: var(--tt-accent);
        }
        .human-verification {
            min-height: 78px;
        }
        .human-verification-error {
            margin: 0;
            color: #9b2e1f;
            font-size: 0.86rem;
            line-height: 1.45;
        }

        .checkout-upsell-zone {
            margin: 30px 0;
            padding: clamp(22px, 3.5cqw, 32px);
            border: 1px solid var(--tt-line);
            border-radius: var(--teqtank-radius-medium, 12px);
            background: var(
                --teqtank-checkout-upsell-background,
                color-mix(in srgb, var(--tt-soft) 58%, #fff)
            );
            color: var(--tt-ink);
        }

        .checkout-upsell-heading {
            margin-bottom: 20px;
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            gap: 24px;
        }

        .checkout-upsell-heading .eyebrow {
            color: var(--tt-accent);
        }

        .checkout-upsell-heading h2 {
            max-width: 620px;
            margin: 4px 0 7px;
            font: 500 clamp(26px, 3.25cqw, 38px)/1.08 var(--tt-display);
        }

        .checkout-upsell-heading p:not(.eyebrow) {
            max-width: 560px;
            margin: 0;
            color: var(--tt-muted);
            font-size: 12px;
            line-height: 1.55;
        }

        .checkout-upsell-heading > span {
            padding: 7px 10px;
            border: 1px solid var(--tt-line);
            border-radius: 100px;
            background: rgba(255, 255, 255, 0.58);
            color: var(--tt-muted);
            font-size: 8px;
            font-weight: 750;
            letter-spacing: 0.1em;
            white-space: nowrap;
        }

        .checkout-upsells {
            display: grid;
            grid-template-columns: minmax(0, 1fr);
            gap: 12px;
        }

        .checkout-upsell-card {
            min-width: 0;
            padding: clamp(16px, 2.5cqw, 22px);
            display: grid;
            grid-template-columns: minmax(0, 1fr);
            grid-template-areas:
                "product"
                "actions"
                "dismiss";
            gap: 14px;
            border: 1px solid var(--tt-line);
            border-radius: var(
                --teqtank-checkout-upsell-card-radius,
                var(--teqtank-radius-small, 8px)
            );
            background: #fff;
            color: var(--tt-ink);
            box-shadow: var(--teqtank-shadow-small, 0 8px 24px rgba(22, 34, 29, 0.06));
            animation: checkout-upsell-enter var(--teqtank-motion-fast, 180ms)
                var(--teqtank-easing-standard, ease-out);
        }

        @keyframes checkout-upsell-enter {
            from {
                opacity: 0;
                transform: translateY(6px);
            }
            to {
                opacity: 1;
                transform: translateY(0);
            }
        }

        .checkout-upsell-product {
            grid-area: product;
            display: grid;
            grid-template-columns: 88px minmax(0, 1fr);
            align-items: center;
            gap: 16px;
            min-width: 0;
        }

        .checkout-upsell-product img,
        .checkout-upsell-image-placeholder {
            width: 88px;
            height: 104px;
            box-sizing: border-box;
            padding: 8px;
            border-radius: var(--teqtank-radius-small, 8px);
            background: #f0eee7;
            object-fit: var(--teqtank-product-image-fit, contain);
        }

        .checkout-upsell-copy {
            min-width: 0;
        }

        .checkout-upsell-copy small {
            color: var(--tt-accent);
            font-size: 8px;
            font-weight: 800;
            letter-spacing: 0.12em;
        }

        .checkout-upsell-copy h3 {
            margin: 6px 0 5px;
            font: 600 18px/1.15 var(--tt-display);
        }

        .checkout-upsell-copy p {
            margin: 0;
            color: var(--tt-muted);
            font-size: 11px;
            line-height: 1.5;
        }

        .checkout-upsell-actions {
            grid-area: actions;
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(min(100%, 9rem), 1fr));
            gap: 8px;
            min-width: 0;
        }

        .checkout-upsell-actions button {
            width: 100%;
            min-width: 0;
            min-height: 48px;
            padding: 9px 12px;
            display: grid;
            grid-template-columns: minmax(0, 1fr) auto;
            grid-template-areas:
                "label price"
                "detail price";
            align-items: center;
            gap: 2px 12px;
            border-radius: var(
                --teqtank-checkout-upsell-action-radius,
                var(--teqtank-radius-small, 8px)
            );
            cursor: pointer;
            line-height: 1.15;
            text-align: left;
            overflow: hidden;
        }

        .upsell-subscribe {
            border: 1px solid var(--tt-ink);
            background: var(--tt-ink);
            color: #fff;
        }

        .upsell-once {
            border: 1px solid rgba(22, 38, 31, 0.2);
            background: transparent;
            color: var(--tt-ink);
        }

        .checkout-upsell-actions button span {
            grid-area: label;
            min-width: 0;
            font-size: 11px;
            font-weight: 750;
            overflow-wrap: anywhere;
        }

        .checkout-upsell-actions button strong {
            grid-area: price;
            font-size: 15px;
            white-space: nowrap;
        }

        .checkout-upsell-actions button small {
            grid-area: detail;
            min-width: 0;
            color: currentColor;
            font-size: 9px;
            line-height: 1.25;
            opacity: 0.72;
            overflow-wrap: anywhere;
        }

        .upsell-dismiss {
            grid-area: dismiss;
            width: max-content;
            max-width: 100%;
            margin: 0;
            padding: 2px 0 4px;
            border: 0;
            border-bottom: 1px solid currentColor;
            background: transparent;
            color: var(--tt-ink);
            font-size: 10px;
            cursor: pointer;
            opacity: 0.78;
        }

        .primary,
        .secondary {
            border: 0;
            cursor: pointer;
            font-weight: 750;
        }

        .primary {
            width: 100%;
            padding: 1rem 1.3rem;
            background: var(--tt-ink);
            color: #fff;
            font-size: 0.95rem;
        }

        .secondary {
            padding: 0.75rem 1rem;
            border: 1px solid var(--tt-line);
            background: transparent;
            color: inherit;
        }

        .primary:disabled,
        .checkout-upsell-actions button:disabled,
        .upsell-dismiss:disabled {
            opacity: 0.55;
            cursor: wait;
        }

        .secure {
            text-align: center;
            color: var(--tt-muted);
            font-size: 11px;
            opacity: 0.75;
        }

        .error {
            margin-bottom: 1rem;
            padding: 0.85rem 1rem;
            background: #fff2ef;
            color: #9b2e1f;
            font-size: 0.85rem;
        }

        .validation {
            margin: 0.8rem 0;
            padding-left: 1.2rem;
            color: #9b2e1f;
            font-size: 0.8rem;
        }

        .loading,
        .empty,
        .complete {
            max-width: 780px;
            margin: 4rem auto;
            padding: 3rem;
            border: 1px solid var(--tt-line);
            text-align: center;
        }

        .empty a,
        .complete a {
            display: inline-flex;
            margin-top: 1rem;
            color: inherit;
            font-weight: 700;
        }

        .account-signin {
            margin: 0 0 24px;
        }
        .account-note {
            display: flex;
            justify-content: space-between;
            gap: 1rem;
            align-items: center;
            padding: 14px 16px;
            border: 1px solid var(--tt-line);
            border-radius: 12px;
            background: var(--tt-soft);
            font-size: 12px;
        }
        .account-note button {
            border: 0;
            border-radius: 999px;
            padding: 9px 13px;
            font: inherit;
            font-weight: 700;
            cursor: pointer;
        }
        .saved-payments {
            display: grid;
            gap: 4px;
            margin: 0 0 20px;
        }
        .saved-payment {
            display: grid;
            grid-template-columns: 18px minmax(0, 1fr);
            gap: 14px;
            align-items: center;
            padding: 14px 16px;
            border: 1px solid transparent;
            border-radius: var(--teqtank-checkout-option-radius, var(--tt-field-radius));
            background: transparent;
            cursor: pointer;
            transition:
                border-color var(--teqtank-motion-fast, 120ms) ease,
                background var(--teqtank-motion-fast, 120ms) ease;
        }
        .saved-payment:hover {
            background: color-mix(in srgb, var(--tt-soft) 62%, transparent);
        }
        .saved-payment:has(input:checked) {
            border-color: color-mix(in srgb, var(--tt-accent) 32%, transparent);
            background: color-mix(in srgb, var(--tt-accent) 7%, transparent);
        }
        .saved-payment input[type="radio"] {
            width: 18px;
            height: 18px;
            min-height: 0;
            margin: 0;
            padding: 0;
            border: 0;
            border-radius: 50%;
            box-shadow: none;
            accent-color: var(--tt-accent);
        }
        .saved-payment span {
            display: grid;
            gap: 2px;
        }
        .saved-payment small {
            color: var(--tt-muted);
        }
        .payment-header {
            margin-bottom: 1rem;
            display: flex;
            justify-content: space-between;
            gap: 1rem;
            align-items: center;
        }
        .payment-options,
        .points-payment {
            display: grid;
            gap: 0.45rem;
            margin: 0 0 1rem;
        }
        .payment-options select {
            width: 100%;
            border: 1px solid var(--tt-line);
            border-radius: var(--tt-field-radius);
            background: var(--tt-field-background);
            color: var(--tt-ink);
            padding: 0.85rem 1rem;
            font: inherit;
        }
        .points-payment {
            border: 1px solid transparent;
            border-radius: var(--teqtank-checkout-option-radius, var(--tt-field-radius));
            padding: 0.875rem 1rem;
            background: color-mix(in srgb, var(--tt-soft) 58%, transparent);
        }
        .points-payment label {
            display: flex;
            gap: 0.75rem;
            align-items: center;
            cursor: pointer;
        }
        .points-payment small {
            color: var(--tt-muted);
        }
        .points-payment input[type="checkbox"] {
            width: 18px;
            height: 18px;
            min-height: 0;
            margin: 0;
            padding: 0;
            border: 0;
            box-shadow: none;
            accent-color: var(--tt-accent);
        }
        .billing-match {
            display: flex;
            align-items: flex-start;
            gap: 0.75rem;
            margin: 0 0 1rem;
            padding: 0.875rem 1rem;
            border: 1px solid transparent;
            border-radius: var(--teqtank-checkout-option-radius, var(--tt-field-radius));
            background: color-mix(in srgb, var(--tt-soft) 58%, transparent);
            cursor: pointer;
        }
        .billing-match input {
            flex: 0 0 auto;
            width: 18px;
            height: 18px;
            min-height: 0;
            margin: 0.15rem 0 0;
            padding: 0;
            border: 0;
            box-shadow: none;
            accent-color: var(--tt-accent);
        }
        .billing-match span {
            display: grid;
            gap: 0.2rem;
        }
        .billing-match small {
            color: var(--tt-muted);
            line-height: 1.45;
        }

        /* Responsive behavior is based on the space given to the component, not the browser/device. */

        .summary-column {
            display: grid;
            grid-template-rows: minmax(0, 1fr) auto;
            gap: 14px;
            align-content: stretch;
            align-self: start;
            min-width: 0;
            max-height: var(
                --teqtank-checkout-summary-max-height,
                calc(
                    100dvh - var(--teqtank-checkout-summary-offset, 20px) - var(
                            --teqtank-checkout-summary-bottom-gap,
                            20px
                        )
                )
            );
            position: sticky;
            top: var(--teqtank-checkout-summary-offset, 20px);
            z-index: 1;
        }

        .summary-column > .summary {
            --teqtank-checkout-summary-max-height: 100%;
            min-height: 0;
            max-height: 100%;
        }

        .share-cart {
            border: 1px solid var(--tt-line);
            border-radius: 16px;
            padding: 14px;
            background: color-mix(in srgb, var(--tt-surface) 96%, var(--tt-accent));
            box-shadow: 0 12px 34px color-mix(in srgb, var(--tt-ink) 8%, transparent);
        }

        .share-cart-trigger {
            width: 100%;
            border: 0;
            border-radius: 12px;
            padding: 13px 16px;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 9px;
            background: transparent;
            color: var(--tt-ink);
            font-weight: 800;
            cursor: pointer;
            transition:
                transform 180ms ease,
                background 180ms ease;
        }

        .share-cart-trigger:hover {
            transform: translateY(-1px);
            background: var(--tt-soft);
        }

        .share-cart-panel {
            position: fixed;
            inset: 0 0 0 auto;
            margin: 0 0 0 auto;
            width: min(460px, 100%);
            max-width: 100%;
            height: 100dvh;
            max-height: 100dvh;
            box-sizing: border-box;
            padding: clamp(20px, 4cqw, 36px);
            border: 0;
            overflow: auto;
            color: var(--tt-ink);
            background: var(--tt-surface);
            box-shadow: -18px 0 50px color-mix(in srgb, var(--tt-ink) 18%, transparent);
            animation: checkout-address-reveal 220ms ease-out both;
        }

        .share-cart-panel::backdrop {
            background: color-mix(in srgb, var(--tt-ink) 42%, transparent);
        }

        .share-cart-head {
            margin-bottom: 18px;
            display: flex;
            justify-content: space-between;
            gap: 16px;
            align-items: start;
        }

        .share-cart-head h2 {
            margin: 4px 0 0;
            font: 500 28px var(--tt-display);
        }
        .share-cart-close {
            border: 0;
            background: transparent;
            font-size: 28px;
            cursor: pointer;
        }

        .share-cart-preview {
            margin-bottom: 16px;
            padding: 16px;
            display: flex;
            gap: 12px;
            align-items: center;
            border-radius: 14px;
            background: var(--tt-soft);
        }

        .share-cart-images {
            display: flex;
        }
        .share-cart-images img,
        .share-cart-images span {
            width: 46px;
            height: 54px;
            margin-left: -10px;
            border: 2px solid var(--tt-soft);
            border-radius: 8px;
            object-fit: cover;
            background: #fff;
        }
        .share-cart-images > :first-child {
            margin-left: 0;
        }

        .purchase-zone {
            margin-top: 10px;
        }

        .purchase-cta {
            width: 100%;
            border: 1px solid var(--tt-accent);
            border-radius: 14px;
            padding: 16px 20px;
            background: var(--tt-accent);
            color: white;
            font: 800 15px var(--tt-body);
            cursor: pointer;
            box-shadow: 0 14px 30px color-mix(in srgb, var(--tt-accent) 24%, transparent);
            transition:
                transform 180ms ease,
                box-shadow 180ms ease,
                opacity 180ms ease;
        }

        .purchase-cta:hover:not(:disabled) {
            transform: translateY(-2px);
            box-shadow: 0 18px 38px color-mix(in srgb, var(--tt-accent) 30%, transparent);
        }

        .purchase-cta:disabled {
            opacity: 0.6;
            cursor: wait;
        }

        @container checkout (max-width: 1080px) {
            .shell {
                grid-template-columns: 1fr;
                gap: 30px;
            }

            main {
                max-width: none;
            }

            .summary-column {
                --teqtank-checkout-summary-max-height: none;
                position: static;
                max-height: none;
            }

            .summary-column > .summary {
                --teqtank-checkout-summary-max-height: none;
                max-height: none;
            }

            .checkout-upsell-heading {
                align-items: flex-start;
                flex-direction: column;
            }
        }

        @container checkout (max-width: 720px) {
            .shell {
                padding: 24px 18px 36px;
                gap: 22px;
            }

            h1 {
                font-size: clamp(34px, 10cqw, 46px);
            }

            .steps {
                margin: 20px 0 30px;
                gap: 12px 18px;
            }

            .section {
                margin-bottom: 32px;
                padding: 0 0 32px;
            }

            .grid {
                grid-template-columns: 1fr;
            }

            .wide {
                grid-column: auto;
            }

            .saved-addresses {
                display: grid;
                gap: 8px;
                margin-bottom: 18px;
            }

            .saved-address {
                display: grid;
                grid-template-columns: auto 1fr;
                gap: 13px;
                padding: 16px;
                border: 1px solid var(--tt-line);
                background: #fff;
                cursor: pointer;
            }

            .saved-address:has(input:checked) {
                border-color: var(--tt-accent);
                box-shadow: inset 0 0 0 1px var(--tt-accent);
            }

            .saved-address input {
                width: auto;
            }
            .saved-address strong,
            .saved-address small {
                display: block;
            }
            .saved-address small {
                margin-top: 3px;
                color: var(--tt-muted);
            }

            .account-gate {
                margin: 20px 0;
                padding: 20px;
                border: 1px solid var(--tt-line);
                background: #fff;
            }

            .account-gate h3 {
                margin: 0 0 8px;
                font: 500 22px var(--tt-display);
            }
            .account-gate p {
                margin: 0 0 16px;
                color: var(--tt-muted);
            }

            .checkout-upsell-zone {
                margin: 22px 0;
                padding: 20px;
            }

            .checkout-upsell-heading {
                margin-bottom: 18px;
                gap: 14px;
            }

            .checkout-upsell-heading h2 {
                font-size: clamp(30px, 9cqw, 40px);
            }

            .checkout-upsell-heading > span {
                white-space: normal;
            }

            .checkout-upsell-product {
                grid-template-columns: 76px minmax(0, 1fr);
            }

            .checkout-upsell-product img,
            .checkout-upsell-image-placeholder {
                width: 76px;
                height: 90px;
            }

            .payment-header {
                align-items: flex-start;
                flex-direction: column;
            }
        }

        @container checkout (max-width: 460px) {
            .shell {
                padding-inline: 14px;
            }

            .checkout-upsell-card {
                padding: 13px;
            }

            .method {
                grid-template-columns: auto minmax(0, 1fr);
            }

            .method > b {
                grid-column: 2;
            }
        }

        @container checkout (max-width: 340px) {
            .checkout-upsell-zone {
                padding: 14px;
            }

            .checkout-upsell-product {
                grid-template-columns: minmax(0, 1fr);
                gap: 10px;
            }

            .checkout-upsell-product img,
            .checkout-upsell-image-placeholder {
                width: 100%;
                height: 112px;
            }
        }
    `;

    protected override firstUpdated(): void {
        void this.initialize();
    }

    protected override updated(): void {
        this.observeInformationValues();
        void this.ensureRecaptcha().catch((error: unknown) => {
            if (this.recaptchaInitializationFailed) return;
            this.recaptchaInitializationFailed = true;
            const message = this.describe(error);
            this.humanVerificationError = message;
            this.submissionErrorMessage = message;
        });
    }

    public override disconnectedCallback(): void {
        this.unsubscribeMarket?.();
        this.unsubscribeMarket = undefined;
        this.unsubscribeCheckout?.();
        this.unsubscribeCheckout = undefined;
        this.unsubscribeCart?.();
        this.unsubscribeCart = undefined;
        if (this.calculationTimer !== undefined) {
            clearTimeout(this.calculationTimer);
        }
        this.stopObservingInformationValues();
        if (this.sponsorSearchTimer !== undefined) clearTimeout(this.sponsorSearchTimer);
        this.releaseRecaptcha();
        if (this.ownsCommerce) this.commerce?.dispose();
        super.disconnectedCallback();
    }

    protected override render() {
        if (this.loading)
            return html`<div class="loading" part="loading">Preparing secure checkout…</div>`;
        if (this.step === "complete")
            return html`<teqtank-order-complete
                part="order-complete"
                .commerce=${this.commerce}
                .orderId=${this.completedOrderId}
                .commerceKey=${this.commerceKey}
                .dataSource=${this.dataSource}
                .shopRoute=${this.shopRoute}
                .continuationRoute=${this.enrollmentContinuationRoute()}
                .continuationLabel=${
                    this.enrollmentContinuation
                        ? this.enrollmentContinuation.status === "completed" ||
                          this.enrollmentContinuation.currentStage.kind === "complete"
                            ? "View enrollment confirmation"
                            : "Continue enrollment"
                        : ""
                }
                .continuationStageId=${this.enrollmentContinuation?.currentStage.id ?? ""}
                .continuationStageKind=${this.enrollmentContinuation?.currentStage.kind ?? ""}
            ></teqtank-order-complete>`;
        if (this.checkout === undefined)
            return html`<div class="empty" part="empty">
                <p class="eyebrow" part="eyebrow">YOUR BAG IS EMPTY</p>
                <h1 part="title">Begin your ritual.</h1>
                <p>Add something from the collection before starting checkout.</p>
                <a part="primary-action" href=${this.shopRoute}>Continue shopping</a>
            </div>`;

        const checkout = this.checkout;
        const calculated = this.hasAuthoritativeCalculation(checkout);
        const calculationErrors = this.calculationErrors(checkout);
        return html`
            <section class="shell" part="checkout">
                <main part="main">
                    ${
                        this.isEnrollmentCheckout()
                            ? html`<button
                                  class="back"
                                  part="back"
                                  type="button"
                                  ?disabled=${this.busy}
                                  @click=${this.continueEnrollmentShopping}
                              >
                                  ←
                                  ${
                                      this.restartsEnrollmentFromCheckout()
                                          ? "Choose a different pack"
                                          : "Continue enrollment shopping"
                                  }
                              </button>`
                            : html`<a class="back" part="back" href=${this.shopRoute}
                                  >← Continue shopping</a
                              >`
                    }
                    <p class="eyebrow" part="eyebrow">SECURE CHECKOUT</p>
                    <h1 part="title">Complete your ritual.</h1>
                    <div class="steps" part="steps">
                        <span class=${this.step === "information" ? "active" : ""}
                            >1 Information</span
                        >
                        <span class=${this.step === "information" ? "active" : ""}>2 Delivery</span>
                        <span class="active">3 Payment</span>
                    </div>
                    ${
                        this.errorMessage
                            ? html`<div class="error" part="error" role="alert">
                                  ${this.errorMessage}
                              </div>`
                            : nothing
                    }
                    ${
                        this.addressRegionsErrorMessage
                            ? html`<div class="error" part="error" role="alert">
                                  ${this.addressRegionsErrorMessage}
                              </div>`
                            : nothing
                    }
                    ${this.renderInformation(checkout)}
                </main>
                <aside class="summary-column" part="summary-column">
                    <teqtank-checkout-summary
                        class="summary"
                        part="summary"
                        exportparts="summary,header,item-list,item,item-media,item-content,item-title,item-meta,item-price,purchase-controls,promo,totals,total,error"
                        .checkout=${checkout}
                        .busy=${this.busy}
                        .calculating=${this.calculating}
                        .calculated=${calculated}
                        .calculationErrors=${calculationErrors}
                        .recurringSavings=${this.recurringSavings}
                        @teqtank-checkout-coupon-apply=${this.summaryCouponApply}
                        @teqtank-checkout-coupon-remove=${this.summaryCouponRemove}
                        @teqtank-checkout-purchase-change=${this.summaryPurchaseChange}
                    ></teqtank-checkout-summary>
                    ${
                        this.showShareCart && !this.hideShareCart
                            ? this.renderShareCart(checkout)
                            : nothing
                    }
                </aside>
            </section>
        `;
    }

    private renderInformation(checkout: CommerceCheckout) {
        const customer = this.commerce?.customer.customer;
        const calculated = this.hasAuthoritativeCalculation(checkout);
        const calculationErrors = this.calculationErrors(checkout);
        return html`
            ${
                customer
                    ? html`<div class="account-note" part="account-note">
                          <span>Signed in as <strong>${customer.displayName}</strong></span
                          ><button type="button" @click=${this.signOut}>Sign out</button>
                      </div>`
                    : html`<div class="account-signin" part="account-signin">
                          <teqtank-login
                              part="login"
                              exportparts="card,title,tabs,form,primary-action,secondary-action,error"
                              .commerce=${this.commerce}
                              @teqtank-customer-login=${this.customerLoggedIn}
                          ></teqtank-login>
                      </div>`
            }
            ${
                this.identityAuthenticationRequired && !customer
                    ? html`<div class="account-gate" part="account-gate">
                          <h3>Sign in to continue</h3>
                          <p>
                              Those details are already associated with an account. Sign in with
                              your password or an email verification code to keep this order
                              connected to your account.
                          </p>
                      </div>`
                    : nothing
            }
            ${
                this.accountApplyPrompt
                    ? html`<div class="account-note" part="account-note account-apply-prompt">
                          <span
                              ><strong>Use your saved account details?</strong><br />Replace the
                              contact, shipping and saved payment details currently in this
                              checkout.</span
                          ><span
                              ><button type="button" @click=${this.applyCustomerDetails}>
                                  Use saved details</button
                              ><button type="button" @click=${this.keepEnteredDetails}>
                                  Keep entered details
                              </button></span
                          >
                      </div>`
                    : nothing
            }
            <form
                class="form"
                part="form"
                @input=${this.informationChanged}
                @change=${this.informationChangeCommitted}
                @submit=${this.continueToPayment}
            >
                <section class="section" part="section contact-section">
                    <div class="title" part="section-title">
                        <span>01</span>
                        <h2>Contact</h2>
                    </div>
                    <div class="grid">
                        <label class="wide"
                            >Email address<input
                                name="email"
                                type="email"
                                autocomplete="email"
                                required
                                .value=${
                                    this.editableContact?.email ??
                                    checkout.contact?.email ??
                                    customer?.email ??
                                    ""
                                }
                        /></label>
                        <label class="wide"
                            >Phone number<input
                                name="phone"
                                type="tel"
                                autocomplete="tel"
                                required
                                .value=${
                                    this.editableContact?.phone ??
                                    checkout.contact?.phone ??
                                    customer?.phone ??
                                    ""
                                }
                        /></label>
                        <label class="wide checkbox"
                            ><input
                                name="marketing"
                                type="checkbox"
                                ?checked=${
                                    this.editableContact?.acceptsMarketing ??
                                    checkout.contact?.acceptsMarketing ??
                                    false
                                }
                            />Keep me inspired with ritual tips</label
                        >
                    </div>
                </section>

                <section class="section" part="section delivery-address-section">
                    <div class="title" part="section-title">
                        <span>02</span>
                        <h2>Delivery address</h2>
                    </div>
                    ${
                        customer?.addresses?.length
                            ? this.savedAddresses(customer.addresses, checkout.shippingAddress)
                            : nothing
                    }
                    ${this.recipientFields(
                        this.editableAddress ?? checkout.shippingAddress,
                        customer,
                    )}
                    ${
                        this.newAddressSelected || !customer?.addresses?.length
                            ? this.addressFields(
                                  this.newAddressSelected
                                      ? (this.editableAddress ?? null)
                                      : (this.editableAddress ?? checkout.shippingAddress),
                              )
                            : nothing
                    }
                    ${this.renderAddressValidation(checkout)}
                </section>

                <section class="section" part="section delivery-method-section">
                    <div class="title" part="section-title">
                        <span>03</span>
                        <h2>Delivery method</h2>
                    </div>
                    <div class="delivery" part="delivery-methods">
                        ${
                            !calculated
                                ? calculationErrors.length > 0
                                    ? html`<p class="calculation-note">
                                          Delivery methods are unavailable until the order can be
                                          calculated.
                                      </p>`
                                    : html`<p class="calculation-note">
                                          Complete the contact and delivery address to load
                                          available delivery methods.
                                      </p>`
                                : checkout.shippingMethods.map(
                                      (method) => html`
                                          <label class="method" part="delivery-method">
                                              <input
                                                  type="radio"
                                                  name="shipping"
                                                  value=${method.id}
                                                  ?checked=${method.selected}
                                                  @change=${() => this.selectShipping(method.id)}
                                              />
                                              <span>
                                                  <strong>${method.name}</strong>
                                                  ${method.description ? html`<small>${method.description}</small>` : nothing}
                                                  ${method.estimatedDelivery ? html`<small>${method.estimatedDelivery}</small>` : nothing}
                                              </span>
                                              <b
                                                  >${
                                                      method.price.amount === 0
                                                          ? "FREE"
                                                          : method.price.formatted
                                                  }</b
                                              >
                                          </label>
                                      `,
                                  )
                        }
                    </div>
                </section>

                ${checkout.upsells.length > 0 ? this.renderUpsells(checkout) : nothing}
                ${!customer ? this.renderRegistration() : nothing}
                ${customer ? this.renderConsents() : nothing} ${this.renderPayment(checkout)}
                <div class="purchase-zone" part="purchase-zone">
                    ${
                        this.submissionErrorMessage
                            ? html`<div class="error" part="error submission-error" role="alert">
                                  ${this.submissionErrorMessage}
                              </div>`
                            : nothing
                    }
                    <button
                        class="purchase-cta"
                        part="primary-action purchase-action"
                        type="submit"
                        ?disabled=${this.busy || this.identityAuthenticationRequired}
                    >
                        ${
                            this.paymentProcessing
                                ? "Processing secure payment…"
                                : this.saving
                                  ? "Updating your order…"
                                  : this.commerce?.context.features.has("order-submit")
                                    ? calculated
                                        ? `Complete order securely · ${checkout.totals.total.formatted}`
                                        : "Complete order securely"
                                    : calculated
                                      ? `Review order · ${checkout.totals.total.formatted}`
                                      : "Review order"
                        }
                    </button>
                </div>
                <p class="secure" part="secure-notice">
                    🔒 Products, promotions, pricing, tax, shipping and discounts shown at checkout
                    are authoritative from the Commerce Gateway.
                </p>
            </form>
        `;
    }

    private renderRegistration() {
        const registration = this.registration;
        const passwordMode = registration?.passwordMode ?? this.accountPassword;
        const sponsor = registration?.sponsor;
        return html`<section class="account-gate" part="account-gate registration">
            <h3>New customer?</h3>
            <p>
                Your account will be created securely before the order is submitted. If payment
                needs another attempt, the same account will be reused.
            </p>
            <div class="registration-options">
                ${
                    passwordMode !== "none"
                        ? html`<label
                              >Password${passwordMode === "optional" ? " (optional)" : ""}<input
                                  type="password"
                                  name="accountPassword"
                                  autocomplete="new-password"
                                  ?required=${passwordMode === "required"}
                                  .value=${this.newAccountPassword}
                                  @input=${(event: InputEvent) => (this.newAccountPassword = (event.currentTarget as HTMLInputElement).value)}
                          /></label>`
                        : nothing
                }
                ${
                    sponsor && sponsor.mode !== "hidden" && !sponsor.attributionApplied
                        ? html`
                              <label
                                  >Sponsor${sponsor.mode === "optional" ? " (optional)" : ""}<input
                                      name="sponsorWebAlias"
                                      list="teqtank-sponsor-results"
                                      autocomplete="off"
                                      ?required=${sponsor.mode === "required" && !this.noSponsor}
                                      ?disabled=${this.noSponsor}
                                      .value=${this.sponsorWebAlias}
                                      @input=${this.sponsorChanged}
                              /></label>
                              <datalist id="teqtank-sponsor-results">
                                  ${this.sponsorResults.map((result) => html`<option value=${result.webAlias}>${result.displayName}</option>`)}
                              </datalist>
                              ${
                                  sponsor.allowNoSponsor
                                      ? html`<label class="checkbox"
                                            ><input
                                                type="checkbox"
                                                name="noSponsor"
                                                .checked=${this.noSponsor}
                                                @change=${(event: Event) => {
                                                    this.noSponsor = (
                                                        event.currentTarget as HTMLInputElement
                                                    ).checked;
                                                    if (this.noSponsor) this.sponsorWebAlias = "";
                                                }}
                                            /><span>I do not have a sponsor</span></label
                                        >`
                                      : nothing
                              }
                          `
                        : nothing
                }
                ${this.renderCustomFields()} ${this.renderConsentFields()}
                ${
                    registration?.verification.provider === "recaptcha"
                        ? html`
                              <div class="human-verification">
                                  <slot name="teqtank-recaptcha"></slot>
                              </div>
                              ${
                                  this.humanVerificationError
                                      ? html`<p class="human-verification-error" role="alert">
                                            ${this.humanVerificationError}
                                        </p>`
                                      : nothing
                              }
                          `
                        : nothing
                }
            </div>
        </section>`;
    }

    private renderConsents() {
        if (!this.registration?.consents.length && !this.registration?.customFields?.length)
            return nothing;
        return html`<section class="account-gate" part="account-gate consents">
            <h3>
                ${this.registration?.customFields?.length ? "Enrollment details" : "Order preferences"}
            </h3>
            <div class="registration-options">
                ${this.renderCustomFields()}${this.renderConsentFields()}
            </div>
        </section>`;
    }

    private renderCustomFields() {
        return (
            this.registration?.customFields?.map(
                (field) =>
                    html`<label
                        >${field.label}${field.required ? " *" : ""}<input
                            type=${field.inputType}
                            name=${`custom:${field.id}`}
                            ?required=${field.required}
                            placeholder=${field.placeholder ?? ""}
                            autocomplete=${field.autocomplete ?? "off"}
                            .value=${this.customFieldValues[field.id] ?? ""}
                            @input=${(event: InputEvent) => {
                                this.customFieldValues = Object.freeze({
                                    ...this.customFieldValues,
                                    [field.id]: (event.currentTarget as HTMLInputElement).value,
                                });
                            }}
                        />${field.description ? html`<small>${field.description}</small>` : nothing}</label
                    >`,
            ) ?? nothing
        );
    }

    private renderConsentFields() {
        return (
            this.registration?.consents.map(
                (consent) =>
                    html`<label class="checkbox"
                        ><input
                            type="checkbox"
                            name=${`consent:${consent.id}`}
                            ?required=${consent.required}
                            .checked=${this.consentSelections[consent.id] === true}
                            @change=${(event: Event) => {
                                this.consentSelections = Object.freeze({
                                    ...this.consentSelections,
                                    [consent.id]: (event.currentTarget as HTMLInputElement).checked,
                                });
                            }}
                        /><span
                            >${
                                consent.contentHtml
                                    ? unsafeHTML(sanitizeCommerceHtml(consent.contentHtml))
                                    : consent.label
                            }${consent.required ? " *" : ""}${consent.description ? html`<small>${consent.description}</small>` : nothing}</span
                        ></label
                    >`,
            ) ?? nothing
        );
    }

    private renderUpsells(checkout: CommerceCheckout) {
        return html`
            <section class="checkout-upsell-zone" part="upsells">
                <div class="checkout-upsell-heading" part="upsells-header">
                    <div>
                        <p class="eyebrow">COMPLETE YOUR ROUTINE</p>
                        <h2>One thoughtful addition?</h2>
                        <p>
                            Recommended from what’s already in your bag. Add it now without leaving
                            checkout.
                        </p>
                    </div>
                    <span>PERSONALISED FOR YOUR ORDER</span>
                </div>

                <div class="checkout-upsells" part="upsell-list" aria-live="polite">
                    ${checkout.upsells.map((upsell) => {
                        const subscription = upsell.choices.find(
                            (choice) => choice.purchaseType === "recurring",
                        );
                        const oneTime = upsell.choices.find(
                            (choice) => choice.purchaseType === "one-time",
                        );

                        return html`
                            <article class="checkout-upsell-card" part="upsell-card">
                                <div class="checkout-upsell-product" part="upsell-product">
                                    ${
                                        upsell.image === null
                                            ? html`<div
                                                  class="checkout-upsell-image-placeholder"
                                                  part="upsell-media"
                                              ></div>`
                                            : html`<img
                                                  src=${upsell.image.url}
                                                  alt=${upsell.image.alternativeText}
                                                  part="upsell-media"
                                              />`
                                    }
                                    <div class="checkout-upsell-copy" part="upsell-content">
                                        <small part="upsell-badge"
                                            >${upsell.badge ?? "ROUND OUT THE ROUTINE"}</small
                                        >
                                        <h3 part="upsell-title">${upsell.name}</h3>
                                        <p part="upsell-description">${upsell.description}</p>
                                    </div>
                                </div>

                                <div class="checkout-upsell-actions" part="upsell-actions">
                                    ${
                                        subscription === undefined
                                            ? nothing
                                            : html`<button
                                                  class="upsell-subscribe"
                                                  part="upsell-action recurring-action"
                                                  type="button"
                                                  ?disabled=${this.busy}
                                                  @click=${() =>
                                                      this.respondToUpsell(
                                                          upsell.id,
                                                          true,
                                                          subscription.id,
                                                      )}
                                              >
                                                  <span>${subscription.label}</span>
                                                  <strong>${subscription.price.formatted}</strong>
                                                  ${
                                                      subscription.saving == null
                                                          ? html`<small>Recurring delivery</small>`
                                                          : html`<small
                                                                >Save
                                                                ${subscription.saving.formatted}</small
                                                            >`
                                                  }
                                              </button>`
                                    }
                                    ${
                                        oneTime === undefined
                                            ? nothing
                                            : html`<button
                                                  class="upsell-once"
                                                  part="upsell-action one-time-action"
                                                  type="button"
                                                  ?disabled=${this.busy}
                                                  @click=${() =>
                                                      this.respondToUpsell(
                                                          upsell.id,
                                                          true,
                                                          oneTime.id,
                                                      )}
                                              >
                                                  <span>${oneTime.label}</span>
                                                  <strong>${oneTime.price.formatted}</strong>
                                                  <small>One-time</small>
                                              </button>`
                                    }
                                </div>

                                <button
                                    class="upsell-dismiss"
                                    part="dismiss-action"
                                    type="button"
                                    ?disabled=${this.busy}
                                    @click=${() => this.respondToUpsell(upsell.id, false)}
                                >
                                    No thanks
                                </button>
                            </article>
                        `;
                    })}
                </div>
            </section>
        `;
    }

    private renderShareCart(checkout: CommerceCheckout) {
        return html`
            <section class="share-cart" part="share-cart">
                <button
                    class="share-cart-trigger"
                    part="share-cart-trigger"
                    type="button"
                    @click=${this.toggleShareCart}
                >
                    <span>↗</span>
                    <span>${this.shareCartOpen ? "Close share tools" : "Share this cart"}</span>
                </button>
                ${
                    this.shareCartOpen
                        ? html`<dialog
                              class="share-cart-panel"
                              part="share-cart-panel"
                              aria-labelledby="share-cart-title"
                              @close=${this.handleShareCartClosed}
                              @cancel=${this.handleShareCartClosed}
                          >
                              <div class="share-cart-head" part="share-cart-header">
                                  <div>
                                      <small>SOCIAL COMMERCE</small>
                                      <h2 id="share-cart-title">Share my cart</h2>
                                  </div>
                                  <button
                                      class="share-cart-close"
                                      part="share-cart-close"
                                      type="button"
                                      aria-label="Close"
                                      @click=${this.closeShareCart}
                                  >
                                      ×
                                  </button>
                              </div>
                              <div class="share-cart-preview" part="share-cart-preview">
                                  <div class="share-cart-images">
                                      ${checkout.items
                                          .slice(0, 4)
                                          .map((item) =>
                                              item.image === null
                                                  ? html`<span></span>`
                                                  : html`<img
                                                        src=${item.image.url}
                                                        alt=${item.image.alternativeText}
                                                    />`,
                                          )}
                                  </div>
                                  <div>
                                      <strong>${checkout.cart.totals.itemCount} item ritual</strong
                                      ><br /><small
                                          >${
                                              this.hasAuthoritativeCalculation(checkout)
                                                  ? checkout.totals.total.formatted
                                                  : "Final total calculated at checkout"
                                          }</small
                                      >
                                  </div>
                              </div>
                              <teqtank-share-panel
                                  .commerce=${this.commerce}
                                  type="cart"
                                  .targetId=${checkout.cart.id}
                                  heading="Send this exact cart"
                                  description="Create a link that preserves the products, options and recurring choices in this cart."
                                  auto-create
                              ></teqtank-share-panel>
                          </dialog>`
                        : nothing
                }
            </section>
        `;
    }

    private async toggleShareCart(): Promise<void> {
        if (this.shareCartOpen) {
            this.closeShareCart();
            return;
        }

        this.shareCartOpen = true;
        await this.updateComplete;
        if (this.shareCartDialog?.showModal) {
            this.shareCartDialog.showModal();
            return;
        }

        this.shareCartDialog?.setAttribute("open", "");
    }

    private closeShareCart(): void {
        if (this.shareCartDialog?.open && this.shareCartDialog.close) {
            this.shareCartDialog.close();
        }
        this.shareCartOpen = false;
    }

    private handleShareCartClosed(): void {
        this.shareCartOpen = false;
    }

    private renderPayment(checkout: CommerceCheckout) {
        const payment = this.paymentConfiguration;
        const usesSaved = payment?.selectedSavedPaymentMethodId != null;
        return html`
            <section class="section payment-section">
                <div class="payment-header">
                    <div class="title">
                        <span>04</span>
                        <h2>Payment</h2>
                    </div>
                </div>
                ${
                    payment && payment.points.available.amount > 0
                        ? html`<div class="points-payment">
                              <label>
                                  <input
                                      type="checkbox"
                                      name="checkoutPoints"
                                      .checked=${payment.points.enabled}
                                      @change=${this.pointsChanged}
                                  />
                                  <span
                                      ><strong
                                          >Use points (${payment.points.available.formatted}
                                          available)</strong
                                      ><br /><small
                                          >${payment.points.enabled ? `${payment.points.applied.formatted} applied · ${payment.amountDue.formatted} remaining` : "Apply available points toward this order"}</small
                                      ></span
                                  >
                              </label>
                          </div>`
                        : nothing
                }
                ${this.renderSavedPaymentMethods(checkout)}
                ${
                    payment && !usesSaved
                        ? html`<label class="payment-options">
                              <span class="eyebrow">PAYMENT OPTION</span>
                              <select
                                  name="paymentOption"
                                  .value=${payment.selectedPaymentOptionId ?? ""}
                                  @change=${this.paymentOptionChanged}
                                  ?disabled=${this.busy}
                              >
                                  <option value="" disabled>Select a payment option</option>
                                  ${payment.options.map((option) => html`<option value=${option.id}>${option.name}</option>`)}
                              </select>
                              ${
                                  payment.options.find(
                                      (option) => option.id === payment.selectedPaymentOptionId,
                                  )?.description
                                      ? html`<small
                                            >${payment.options.find((option) => option.id === payment.selectedPaymentOptionId)?.description}</small
                                        >`
                                      : nothing
                              }
                          </label>`
                        : nothing
                }
                ${
                    payment && !usesSaved && payment.frameSource
                        ? html`<label class="billing-match">
                              <input
                                  type="checkbox"
                                  name="billingSameAsShipping"
                                  .checked=${this.billingSameAsShipping}
                                  @change=${this.billingSameAsShippingChanged}
                                  ?disabled=${this.busy}
                              />
                              <span>
                                  <strong>Use delivery address as billing address</strong>
                                  <small
                                      >Leave this off to enter a different billing address securely
                                      with your card details.</small
                                  >
                              </span>
                          </label>`
                        : nothing
                }
                <teqtank-payment
                    .commerce=${this.commerce}
                    .checkout=${checkout}
                    .billingAddress=${this.billingSameAsShipping ? checkout.shippingAddress : null}
                    .frameSource=${payment?.frameSource ?? ""}
                    .frameUri=${this.paymentFrameOrigins}
                    .redirectLoader=${this.paymentRedirectLoader}
                    .paymentStyles=${this.paymentStyles}
                    .mockMode=${this.mockPayment}
                    .showSubmit=${false}
                    .hideError=${true}
                    .hideFrame=${!payment?.frameSource}
                    @teqtank-payment-complete=${this.paymentComplete}
                    @teqtank-payment-failed=${this.paymentFailed}
                    @teqtank-payment-cancelled=${this.paymentCancelled}
                ></teqtank-payment>
            </section>
        `;
    }

    private renderSavedPaymentMethods(checkout: CommerceCheckout) {
        const methods = this.commerce?.customer.customer?.savedPaymentMethods ?? [];
        return html`
            <div class="saved-payments">
                ${methods.length > 0 ? html`<p class="eyebrow">SAVED PAYMENT METHODS</p>` : nothing}
                ${methods.map(
                    (method) => html`
                        <label class="saved-payment">
                            <input
                                type="radio"
                                name="savedPayment"
                                .value=${method.id}
                                .checked=${checkout.selectedSavedPaymentMethodId === method.id}
                                @change=${() => this.selectPayment(null, method.id)}
                            />
                            <span>
                                <strong>${method.displayName}</strong>
                                <small
                                    >${method.brand ?? method.type}${
                                        method.expiryMonth && method.expiryYear
                                            ? ` · Expires ${String(method.expiryMonth).padStart(2, "0")}/${String(method.expiryYear).slice(-2)}`
                                            : ""
                                    }</small
                                >
                            </span>
                        </label>
                    `,
                )}
                <label class="saved-payment">
                    <input
                        type="radio"
                        name="savedPayment"
                        value=""
                        .checked=${checkout.selectedSavedPaymentMethodId === null}
                        @change=${() => this.selectNewPayment()}
                    />
                    <span>
                        <strong>Use another payment method</strong>
                        <small>Enter payment details securely below</small>
                    </span>
                </label>
            </div>
        `;
    }

    private readonly informationChanged = (event: Event): void => {
        const field = event.target as HTMLInputElement | HTMLSelectElement;
        const form = event.currentTarget as HTMLFormElement;
        if (field.name === "marketing") {
            this.captureEditableInformation(form);
            this.informationDirty = true;
            return;
        }
        if (!this.isCalculationField(field.name)) return;
        this.captureEditableInformation(form);
        this.rememberCalculationValues(form);
        this.informationRevision += 1;
        this.informationDirty = true;
        this.calculationStale = true;
        this.calculationErrorMessage = "";
        this.submissionErrorMessage = "";
        if (this.isIdentityField(field.name)) {
            // The authentication gate applies only to the identity values that
            // produced it. A changed identity must be resolved again on submit.
            this.identityAuthenticationRequired = false;
            this.newAccountRequired = false;
        }
        this.scheduleAutomaticCalculation(event.currentTarget as HTMLFormElement);
    };

    private readonly informationChangeCommitted = (event: Event): void => {
        const field = event.target as HTMLInputElement | HTMLSelectElement;
        if (!this.isCalculationField(field.name) || field.name === "countryCode") return;
        const form = event.currentTarget as HTMLFormElement;
        this.scheduleAutomaticCalculation(form, 0);
    };

    private isCalculationField(name: string): boolean {
        return calculationFieldNames.includes(name as CalculationFieldName);
    }

    private isIdentityField(name: string): boolean {
        return (this.registration?.identityFields ?? this.identityValidationFields).includes(
            name as CommerceCustomerIdentityField,
        );
    }

    private readonly customerLoggedIn = async (): Promise<void> => {
        this.cancelScheduledCalculation();
        this.releaseRecaptcha();
        this.identityAuthenticationRequired = false;
        this.newAccountRequired = false;
        const commerce = this.requireCommerce();
        this.checkout = (await commerce.checkout.get()) ?? (await commerce.checkout.begin());
        this.calculationStale = !this.hasCalculationResponse(this.checkout);
        const checkoutHasDetails =
            this.checkout?.contact != null ||
            this.checkout?.shippingAddress != null ||
            this.checkout?.billingAddress != null;
        if (this.informationDirty || checkoutHasDetails) {
            this.accountApplyPrompt = true;
            return;
        }

        await this.applyCustomerDetails();
    };

    private readonly applyCustomerDetails = async (): Promise<void> => {
        this.saving = true;
        this.errorMessage = "";
        this.calculationErrorMessage = "";

        try {
            this.checkout = await this.requireCommerce().checkout.applyCustomer();
            this.editableContact = undefined;
            this.editableAddress = undefined;
            this.calculationStale = !this.hasCalculationResponse(this.checkout);
            await this.loadRecurringSavings();
            this.accountApplyPrompt = false;
            this.informationDirty = false;
            await this.calculateCurrentCheckoutIfReady();
        } catch (error) {
            this.errorMessage =
                error instanceof Error ? error.message : "Could not apply account details.";
        } finally {
            this.saving = false;
        }
    };

    private readonly keepEnteredDetails = async (): Promise<void> => {
        this.accountApplyPrompt = false;
        await this.updateComplete;
        const form = this.shadowRoot?.querySelector<HTMLFormElement>("form.form");
        if (form) this.scheduleAutomaticCalculation(form, 0);
    };

    private readonly selectPayment = async (
        paymentOptionId: string | null,
        paymentMethodId: string | null,
    ): Promise<void> => {
        this.saving = true;
        try {
            const commerce = this.requireCommerce();
            this.paymentConfiguration = await commerce.checkout.selectPaymentOption(
                paymentOptionId,
                paymentMethodId,
            );
            this.checkout = (await commerce.checkout.get()) ?? this.checkout;
            this.errorMessage = "";
        } catch (error) {
            this.errorMessage = this.describe(error);
        } finally {
            this.saving = false;
        }
    };

    private readonly selectNewPayment = (): void => {
        const first =
            this.paymentConfiguration?.selectedPaymentOptionId ??
            this.paymentConfiguration?.options[0]?.id ??
            null;
        void this.selectPayment(first, null);
    };

    private readonly paymentOptionChanged = (event: Event): void => {
        void this.selectPayment((event.currentTarget as HTMLSelectElement).value, null);
    };

    private readonly billingSameAsShippingChanged = (event: Event): void => {
        this.billingSameAsShipping = (event.currentTarget as HTMLInputElement).checked;
    };

    private readonly pointsChanged = async (event: Event): Promise<void> => {
        this.saving = true;
        try {
            const commerce = this.requireCommerce();
            this.paymentConfiguration = await commerce.checkout.applyPoints(
                (event.currentTarget as HTMLInputElement).checked,
            );
            this.checkout = (await commerce.checkout.get()) ?? this.checkout;
            this.errorMessage = "";
        } catch (error) {
            this.errorMessage = this.describe(error);
        } finally {
            this.saving = false;
        }
    };

    private readonly signOut = async (): Promise<void> => {
        const commerce = this.requireCommerce();
        this.saving = true;
        this.errorMessage = "";
        try {
            await commerce.customer.logout();
            this.requestUpdate();
            this.checkout = commerce.checkout.current ?? undefined;
            this.accountApplyPrompt = false;
            this.identityAuthenticationRequired = false;
            this.newAccountRequired = false;
            this.newAccountPassword = "";
            this.sponsorWebAlias = "";
            this.noSponsor = false;
            this.consentSelections = {};
            this.humanVerificationToken = "";
            this.humanVerificationError = "";
            this.billingSameAsShipping = false;
            this.releaseRecaptcha();
            this.recaptchaRecoveryAttempts = 0;
            this.newAddressSelected = true;
            this.editableContact = undefined;
            this.editableAddress = undefined;
            this.informationDirty = false;
            this.calculationStale = true;
            await this.loadRecurringSavings();
            await this.loadPaymentConfiguration(true);
        } catch (error) {
            this.errorMessage = this.describe(error);
        } finally {
            this.saving = false;
        }
    };

    private readonly continueEnrollmentShopping = async (): Promise<void> => {
        const commerce = this.requireCommerce();
        this.saving = true;
        this.errorMessage = "";
        try {
            const current = commerce.enrollment.current ?? (await commerce.enrollment.resume());
            let enrollment: CommerceEnrollmentSession;
            if (this.restartsEnrollmentFromCheckout(current)) {
                await commerce.enrollment.abandon();
                enrollment = await commerce.enrollment.begin({});
            } else {
                enrollment = await commerce.enrollment.returnToShopping();
            }
            const navigate = this.dispatchEvent(
                new CustomEvent("teqtank-checkout-continue-enrollment", {
                    detail: { enrollment },
                    bubbles: true,
                    composed: true,
                    cancelable: true,
                }),
            );
            if (navigate && typeof window !== "undefined")
                window.location.assign(this.enrollmentRoute);
        } catch (error) {
            this.errorMessage = this.describe(error);
        } finally {
            this.saving = false;
        }
    };

    private restartsEnrollmentFromCheckout(
        enrollment = this.commerce?.enrollment.current,
    ): boolean {
        return (
            enrollment !== null &&
            enrollment !== undefined &&
            !enrollment.stages.some(
                (stage) => stage.kind === "catalogue" || stage.kind === "recurring-offer",
            )
        );
    }

    private isEnrollmentCheckout(): boolean {
        return this.checkout?.experience === "enrollment";
    }

    private enrollmentContinuationRoute(): string {
        const enrollment = this.enrollmentContinuation;
        if (!enrollment) return "";
        const configured = resolveEnrollmentStageRoute(
            this.enrollmentStageRoutes,
            enrollment.currentStage,
        );
        const route =
            configured || this.postCheckoutEnrollmentRoute.trim() || this.enrollmentRoute.trim();
        return route && !isCurrentBrowserRoute(route) ? route : "";
    }

    private savedAddresses(
        addresses: readonly {
            id: string;
            label: string;
            address: CommerceAddress;
        }[],
        selected: CommerceAddress | null,
    ) {
        return html`
            <div class="saved-addresses">
                ${addresses.map(
                    (item) => html`
                        <label class="saved-address">
                            <input
                                type="radio"
                                name="savedAddress"
                                ?checked=${
                                    !this.newAddressSelected &&
                                    this.sameAddress(item.address, selected)
                                }
                                @change=${() => this.selectCustomerAddress(item.address)}
                            />
                            <span
                                ><strong>${item.label}</strong
                                ><small
                                    >${item.address.addressLine1}${
                                        item.address.addressLine2
                                            ? `, ${item.address.addressLine2}`
                                            : ""
                                    }<br />${item.address.city} ${item.address.postalCode}</small
                                ></span
                            >
                        </label>
                    `,
                )}
                <label class="saved-address">
                    <input
                        type="radio"
                        name="savedAddress"
                        ?checked=${this.newAddressSelected}
                        @change=${this.selectNewAddress}
                    />
                    <span
                        ><strong>Add a new address</strong
                        ><small>Use a different delivery address for this order only.</small></span
                    >
                </label>
            </div>
        `;
    }

    private readonly selectCustomerAddress = async (address: CommerceAddress): Promise<void> => {
        this.newAddressSelected = false;
        this.editableAddress = undefined;
        await this.runCalculationAction(async () => {
            this.checkout = await this.requireCommerce().checkout.updateShippingAddress(address);
            this.calculationStale = true;
            await this.calculateCurrentCheckoutIfReady();
        });
    };

    private readonly selectNewAddress = (): void => {
        this.newAddressSelected = true;
        this.editableAddress = undefined;
        this.informationDirty = true;
        this.calculationStale = true;
        this.calculationErrorMessage = "";
    };

    private readonly acceptSuggestedAddress = async (): Promise<void> => {
        const suggested = this.checkout?.validation.suggestedAddress;
        if (!suggested) return;
        await this.prepareExplicitCalculationAction();
        await this.runCalculationAction(async () => {
            const commerce = this.requireCommerce();
            this.newAddressSelected = true;
            this.editableAddress = suggested;
            this.checkout = await commerce.checkout.updateShippingAddress(suggested);
            this.checkout = await commerce.checkout.calculate();
            this.recordCalculation(this.checkout);
            this.informationDirty = false;
        });
    };

    private readonly keepEnteredAddress = async (): Promise<void> => {
        await this.prepareExplicitCalculationAction();
        await this.runCalculationAction(async () => {
            this.checkout = await this.requireCommerce().checkout.bypassAddressValidation();
            this.recordCalculation(this.checkout);
            this.informationDirty = false;
        });
    };

    private sameAddress(left: CommerceAddress, right: CommerceAddress | null): boolean {
        return (
            right !== null &&
            left.addressLine1 === right.addressLine1 &&
            left.postalCode === right.postalCode &&
            left.countryCode === right.countryCode
        );
    }

    private recipientFields(
        address: CommerceAddress | null,
        customer: { firstName?: string; lastName?: string } | null | undefined,
    ) {
        return html`
            <div class="grid recipient-grid">
                <label
                    >First name<input
                        name="firstName"
                        autocomplete="shipping given-name"
                        required
                        .value=${address?.firstName ?? customer?.firstName ?? ""}
                /></label>
                <label
                    >Last name<input
                        name="lastName"
                        autocomplete="shipping family-name"
                        required
                        .value=${address?.lastName ?? customer?.lastName ?? ""}
                /></label>
            </div>
        `;
    }

    private addressFields(address: CommerceAddress | null) {
        const countries = this.commerce?.context.shippingCountries ?? [];
        const addressCountry = address?.countryCode?.toUpperCase();
        const marketCountry = this.commerce?.context.market.countryCode.toUpperCase();
        const selectedCountry = countries.some((country) => country.code === addressCountry)
            ? addressCountry!
            : countries.some((country) => country.code === marketCountry)
              ? marketCountry!
              : (countries[0]?.code ?? "");
        return html`
            <div class="grid address-detail-fields">
                <label class="wide"
                    >Address<input
                        name="addressLine1"
                        autocomplete="shipping address-line1"
                        required
                        .value=${address?.addressLine1 ?? ""}
                /></label>
                <label
                    >City<input
                        name="city"
                        autocomplete="shipping address-level2"
                        required
                        .value=${address?.city ?? ""}
                /></label>
                <label
                    >State /
                    region${
                        this.addressRegions.length > 0
                            ? html`<select
                                  name="region"
                                  autocomplete="shipping address-level1"
                                  required
                                  ?disabled=${this.addressRegionsLoading}
                              >
                                  <option value="" ?selected=${!address?.region}>
                                      Select state / region
                                  </option>
                                  ${this.addressRegions.map(
                                      (region) =>
                                          html`<option
                                              value=${region.code}
                                              ?selected=${region.code === address?.region}
                                          >
                                              ${region.name}
                                          </option>`,
                                  )}
                              </select>`
                            : html`<input
                                  name="region"
                                  autocomplete="shipping address-level1"
                                  required
                                  .value=${address?.region ?? ""}
                              />`
                    }</label
                >
                <label
                    >Postcode<input
                        name="postalCode"
                        autocomplete="shipping postal-code"
                        required
                        .value=${address?.postalCode ?? ""}
                /></label>
                <label
                    >Country<select
                        name="countryCode"
                        autocomplete="shipping country"
                        required
                        @change=${this.countryChanged}
                    >
                        ${countries.map(
                            (country) =>
                                html`<option
                                    value=${country.code}
                                    ?selected=${country.code === selectedCountry}
                                >
                                    ${country.name}
                                </option>`,
                        )}
                    </select></label
                >
            </div>
        `;
    }

    private renderAddressValidation(checkout: CommerceCheckout) {
        if (this.calculationStale || this.calculating) return nothing;
        const suggested = checkout.validation.suggestedAddress;
        const addressInvalid = checkout.validation.errors.some(
            (error) => error.code === "address-invalid",
        );
        if (!suggested && !addressInvalid) return nothing;

        if (!suggested) {
            return html`<aside class="address-validation" role="alert" aria-live="assertive">
                <h3>We couldn't validate this address</h3>
                <p>
                    Review the address for accuracy. If it is correct, you can use it without
                    validation.
                </p>
                <p>
                    <strong>Note:</strong> An unvalidated address may result in shipping delays or
                    additional charges.
                </p>
                <div class="address-validation-actions">
                    <button
                        class="secondary"
                        type="button"
                        ?disabled=${this.busy}
                        @click=${this.keepEnteredAddress}
                    >
                        Use this address anyway
                    </button>
                </div>
            </aside>`;
        }

        return html`<aside class="address-validation" role="alert" aria-live="assertive">
            <h3>We found a suggested address</h3>
            <p>Choose the verified version below, or keep the address you entered.</p>
            <address>
                <strong>${suggested.addressLine1}</strong><br />
                ${suggested.addressLine2 ? html`${suggested.addressLine2}<br />` : nothing}
                ${suggested.city}, ${suggested.region} ${suggested.postalCode}<br />
                ${suggested.countryCode}
            </address>
            <div class="address-validation-actions">
                <button
                    class="primary"
                    type="button"
                    ?disabled=${this.busy}
                    @click=${this.acceptSuggestedAddress}
                >
                    Use suggested address
                </button>
                <button
                    class="secondary"
                    type="button"
                    ?disabled=${this.busy}
                    @click=${this.keepEnteredAddress}
                >
                    Keep my address
                </button>
            </div>
        </aside>`;
    }

    private async initialize(): Promise<void> {
        try {
            if (this.commerce === undefined) {
                this.commerce = await TeqtankCommerce.initialize({
                    commerceKey: this.commerceKey,
                    dataSource: this.dataSource,
                });
                this.ownsCommerce = true;
            }
            this.bindMarket(this.commerce);
            this.bindCheckout(this.commerce);
            await this.acceptSharedCartFromLocation();
            await this.loadRegistration();
            const existing = await this.commerce.checkout.get();
            this.checkout = existing ?? (await this.commerce.checkout.begin());
            this.calculationStale = !this.hasCalculationResponse(this.checkout);
            await this.loadAddressRegions(
                this.checkout.shippingAddress?.countryCode ??
                    this.commerce.context.market.countryCode,
            );
            await this.loadRecurringSavings();
            await this.loadPaymentConfiguration(true);
        } catch (error) {
            this.checkout = undefined;
            this.errorMessage = this.describe(error);
        } finally {
            this.loading = false;
            await this.updateComplete;
            const form = this.shadowRoot?.querySelector<HTMLFormElement>("form.form");
            if (
                form &&
                this.checkout?.contact &&
                this.checkout.shippingAddress &&
                !this.hasCalculationResponse(this.checkout)
            ) {
                this.scheduleAutomaticCalculation(form, 0);
            }
        }
    }

    private bindMarket(commerce: Commerce): void {
        this.unsubscribeMarket?.();
        this.unsubscribeMarket = commerce.market.onChanged(() => {
            void this.reloadAfterMarketChange();
        });
    }

    private bindCheckout(commerce: Commerce): void {
        this.unsubscribeCheckout?.();
        this.unsubscribeCart?.();
        this.unsubscribeCheckout = commerce.checkout.onChanged((event) => {
            if ((event.reason !== "loaded" && event.reason !== "begun") || this.loading) return;

            void this.adoptExternalCheckout(event.checkout).catch((error: unknown) => {
                if (!this.isConnected) return;
                this.errorMessage = this.describe(error);
            });
        });
        this.unsubscribeCart = commerce.cart.onChanged((event) => {
            if (this.loading || event.cart.lines.length > 0) return;

            this.checkout = undefined;
            this.paymentConfiguration = undefined;
            this.calculationStale = true;
        });
    }

    private async adoptExternalCheckout(checkout: CommerceCheckout): Promise<void> {
        const revision = ++this.externalCheckoutRevision;
        this.checkout = checkout;
        this.calculationStale = !this.hasCalculationResponse(checkout);
        await this.refreshExternalCheckoutDependencies();
        await this.updateComplete;
        const current = this.checkout;

        if (
            revision !== this.externalCheckoutRevision ||
            !current?.contact ||
            !current.shippingAddress ||
            this.hasCalculationResponse(current)
        ) {
            return;
        }

        if (!this.informationDirty) {
            try {
                await this.calculateCurrentCheckoutIfReady();
            } catch (error) {
                this.calculationStale = true;
                this.calculationErrorMessage = this.describe(error);
            }
            return;
        }

        const form = this.shadowRoot?.querySelector<HTMLFormElement>("form.form");
        if (form) this.scheduleAutomaticCalculation(form, 0);
    }

    private async refreshExternalCheckoutDependencies(): Promise<void> {
        await this.loadRegistration();
        await this.loadRecurringSavings();
        await this.loadPaymentConfiguration(false);
    }

    private async loadRegistration(): Promise<void> {
        const commerce = this.requireCommerce();
        if (
            !commerce.context.features.has("customer-create") &&
            !commerce.context.features.has("order-submit")
        ) {
            this.registration = undefined;
            this.consentSelections = {};
            return;
        }

        const registration = await commerce.customer.getRegistration();
        const applicableIds = new Set(registration.consents.map((consent) => consent.id));
        this.consentSelections = Object.freeze(
            Object.fromEntries(
                Object.entries(this.consentSelections).filter(([id]) => applicableIds.has(id)),
            ),
        );
        this.registration = registration;
    }

    private async reloadAfterMarketChange(): Promise<void> {
        this.cancelScheduledCalculation();
        this.paymentConfiguration = undefined;
        const commerce = this.requireCommerce();
        this.checkout = (await commerce.checkout.get()) ?? (await commerce.checkout.begin());
        this.calculationStale = !this.hasCalculationResponse(this.checkout);
        await this.loadAddressRegions(
            this.checkout.shippingAddress?.countryCode ?? commerce.context.market.countryCode,
        );
        await this.loadRegistration();
        await this.loadRecurringSavings();
        await this.loadPaymentConfiguration(true);
    }

    private async acceptSharedCartFromLocation(): Promise<void> {
        if (typeof window === "undefined" || !this.commerce) return;
        const url = new URL(window.location.href);
        const shareId = url.searchParams.get("share")?.trim();
        if (!shareId) return;

        const preview = await this.commerce.share.resolve(shareId);
        const countryCode = preview.resource.metadata.countryCode;
        const currencyCode = preview.resource.metadata.currencyCode;
        if (countryCode && currencyCode) {
            const current = this.commerce.context;
            if (
                current.market.countryCode !== countryCode ||
                current.currency.code !== currencyCode
            ) {
                const switched = await this.commerce.market.switch({
                    countryCode,
                    currencyCode,
                    confirmCartClear: true,
                });
                if (switched.requiresConfirmation) {
                    throw new Error("The shared cart market could not be selected.");
                }
            }
        }

        await this.commerce.share.accept(shareId, "replace");
        url.searchParams.delete("share");
        window.history.replaceState(window.history.state, "", url);
    }

    private async selectShipping(id: string): Promise<void> {
        await this.runCalculationAction(async () => {
            this.checkout = await this.requireCommerce().checkout.selectShippingMethod(id);
            this.recordCalculation(this.checkout);
        });
    }

    private async applyCoupon(code: string): Promise<void> {
        if (!code.trim() || !this.checkout || !this.hasAuthoritativeCalculation(this.checkout))
            return;

        await this.runCalculationAction(async () => {
            this.checkout = await this.requireCommerce().checkout.applyCoupon(code);
            this.recordCalculation(this.checkout);
        }, true);
    }

    private async removeCoupon(code: string): Promise<void> {
        await this.runCalculationAction(async () => {
            this.checkout = await this.requireCommerce().checkout.removeCoupon(code);
            this.recordCalculation(this.checkout);
        }, true);
    }

    private async respondToUpsell(
        upsellId: string,
        accepted: boolean,
        choiceId?: string,
    ): Promise<void> {
        await this.runCalculationAction(async () => {
            this.checkout = await this.requireCommerce().checkout.respondToUpsell(
                upsellId,
                accepted,
                choiceId,
            );
            await this.loadRegistration();
            await this.loadRecurringSavings();
            await this.loadPaymentConfiguration(false);
        });
    }

    private async updatePurchase(
        lineId: string,
        purchaseType: "one-time" | "recurring",
        frequency?: CommerceRecurringFrequencyOption,
    ): Promise<void> {
        await this.runCalculationAction(async () => {
            const commerce = this.requireCommerce();
            this.checkout = await commerce.checkout.updateItemPurchase(
                lineId,
                purchaseType,
                purchaseType === "recurring" ? frequency : undefined,
            );
            this.calculationStale = true;
            if (this.checkout.contact !== null && this.checkout.shippingAddress !== null) {
                this.checkout = await commerce.checkout.calculate();
                this.recordCalculation(this.checkout);
            }
            await this.loadRegistration();
            await this.loadRecurringSavings();
        });
    }

    private readonly summaryCouponApply = (event: CustomEvent<{ code: string }>): void => {
        void this.applyCoupon(event.detail.code);
    };

    private readonly summaryCouponRemove = (event: CustomEvent<{ code: string }>): void => {
        void this.removeCoupon(event.detail.code);
    };

    private readonly summaryPurchaseChange = (
        event: CustomEvent<{
            lineId: string;
            purchaseType: "one-time" | "recurring";
            frequency?: CommerceRecurringFrequencyOption;
            frequencyDays?: number;
        }>,
    ): void => {
        void this.updatePurchase(
            event.detail.lineId,
            event.detail.purchaseType,
            event.detail.frequency,
        );
    };

    private readonly continueToPayment = async (event: SubmitEvent): Promise<void> => {
        event.preventDefault();
        const commerce = this.requireCommerce();
        const checkout = this.checkout;
        if (
            !checkout ||
            !this.hasAuthoritativeCalculation(checkout) ||
            !checkout.validation.isValid ||
            checkout.shippingAddress === null
        ) {
            this.submissionErrorMessage =
                "Complete the required contact and delivery details, then wait for the order totals to finish updating.";
            return;
        }
        const address = checkout.shippingAddress;
        const form = new FormData(event.currentTarget as HTMLFormElement);
        this.dispatchEvent(
            new CustomEvent("teqtank-checkout-ready", {
                detail: { checkout: this.checkout },
                bubbles: true,
                composed: true,
            }),
        );
        if (!commerce.context.features.has("order-submit")) return;
        const paymentElement = this.paymentElement;
        if (!paymentElement) {
            this.submissionErrorMessage = "Secure payment is not ready yet.";
            return;
        }
        if (!paymentElement.preparePaymentWindow()) {
            this.submissionErrorMessage = "Secure payment is still loading. Try again in a moment.";
            return;
        }

        let handedToPayment = false;
        try {
            await this.runSubmissionAction(async () => {
                if (!commerce.customer.isAuthenticated) {
                    if (!commerce.context.features.has("customer-create")) {
                        this.identityAuthenticationRequired = true;
                        this.submissionErrorMessage =
                            "Sign in with a verified customer account to continue.";
                        return;
                    }
                    const identity = await commerce.customer.resolveIdentity({
                        email: String(form.get("email") ?? ""),
                        phone: String(form.get("phone") ?? ""),
                        fields: this.registration?.identityFields ?? this.identityValidationFields,
                    });
                    this.identityAuthenticationRequired =
                        identity.status === "authentication-required";
                    this.newAccountRequired = identity.status === "new-customer";
                    if (this.identityAuthenticationRequired) {
                        this.submissionErrorMessage =
                            "An account already exists for these details. Sign in with your password or an email verification code to continue.";
                        return;
                    }
                    if (this.newAccountRequired) {
                        const passwordMode =
                            this.registration?.passwordMode ?? this.accountPassword;
                        if (passwordMode === "required" && !this.newAccountPassword) {
                            this.submissionErrorMessage = "Create a password to continue.";
                            return;
                        }
                        const verificationToken = this.currentHumanVerificationResponse();
                        if (
                            this.registration?.verification.provider === "recaptcha" &&
                            !verificationToken
                        ) {
                            this.resetIncompleteHumanVerification();
                            if (!this.humanVerificationError) {
                                this.humanVerificationError =
                                    "Human verification was not completed or has expired. Complete it again before submitting the order.";
                            }
                            this.submissionErrorMessage =
                                "Complete the human verification to continue.";
                            return;
                        }
                        try {
                            await commerce.customer.create({
                                email: this.checkout!.contact!.email,
                                phone: this.checkout!.contact!.phone,
                                firstName: address.firstName,
                                lastName: address.lastName,
                                passwordRequired: passwordMode === "required",
                                ...(this.newAccountPassword
                                    ? { password: this.newAccountPassword }
                                    : {}),
                                ...(this.registration?.sponsor.attributionApplied === true
                                    ? {}
                                    : this.noSponsor
                                      ? { sponsor: { noSponsor: true } }
                                      : this.sponsorWebAlias.trim()
                                        ? { sponsor: { webAlias: this.sponsorWebAlias.trim() } }
                                        : {}),
                                ...(this.registration?.verification.provider === "recaptcha"
                                    ? {
                                          verification: {
                                              provider: "recaptcha",
                                              token: verificationToken,
                                          },
                                      }
                                    : {}),
                                ...(this.registration
                                    ? { consents: this.currentConsentSelections() }
                                    : {}),
                                ...(this.registration?.customFields?.length
                                    ? { customFields: this.currentCustomFields() }
                                    : {}),
                            });
                        } finally {
                            if (this.registration?.verification.provider === "recaptcha")
                                this.consumeHumanVerificationResponse();
                        }
                        this.newAccountRequired = false;
                    }
                }
                this.checkout = (await commerce.checkout.get()) ?? this.checkout;
                this.checkout = await commerce.checkout.calculate();
                this.recordCalculation(this.checkout);
                if (!this.checkout.validation.isValid) {
                    this.submissionErrorMessage =
                        "The order changed during its final check. Review the highlighted checkout details and try again.";
                    return;
                }
                this.informationDirty = false;
                this.paymentProcessing = true;
                handedToPayment = true;
                await paymentElement.submit({
                    consents: this.currentConsentSelections(),
                    customFields: this.currentCustomFields(),
                    billingAddress: this.billingSameAsShipping
                        ? this.checkout.shippingAddress
                        : null,
                });
            });
        } finally {
            if (!handedToPayment) paymentElement.closePreparedPaymentWindow();
        }
    };

    private captureEditableInformation(formElement: HTMLFormElement): void {
        const form = new FormData(formElement);
        this.editableContact = {
            email: String(form.get("email") ?? ""),
            phone: String(form.get("phone") ?? ""),
            acceptsMarketing: form.get("marketing") !== null,
        };
        if (!form.has("addressLine1")) return;

        const current = this.editableAddress ?? this.checkout?.shippingAddress;
        const addressLine2 = String(form.get("addressLine2") ?? "").trim();
        this.editableAddress = {
            firstName: String(form.get("firstName") ?? ""),
            lastName: String(form.get("lastName") ?? ""),
            addressLine1: String(form.get("addressLine1") ?? ""),
            ...(addressLine2 ? { addressLine2 } : {}),
            city: String(form.get("city") ?? ""),
            region: String(form.get("region") ?? ""),
            postalCode: String(form.get("postalCode") ?? ""),
            countryCode: String(form.get("countryCode") ?? ""),
            ...(current?.phone ? { phone: current.phone } : {}),
        };
    }

    private observeInformationValues(): void {
        const form = this.shadowRoot?.querySelector<HTMLFormElement>("form.form");
        if (!form) {
            this.stopObservingInformationValues();
            this.observedCalculationValues = undefined;
            return;
        }

        if (this.observedCalculationValues === undefined) {
            this.observedCalculationValues = this.readInitialCalculationValues(form);
            if (this.calculationStale && this.calculationInputsValid(form)) {
                // Browser autofill can complete the entire form before the first input/change
                // event or polling snapshot. Treat an initially valid stale form as changed so
                // Checkout cannot remain blocked behind values already visible to the shopper.
                this.observedCalculationValues = this.readCalculationValues(form);
                this.captureEditableInformation(form);
                this.informationRevision += 1;
                this.informationDirty = true;
                this.scheduleAutomaticCalculation(form, 0);
            }
        }
        if (this.informationValueCheckTimer !== undefined) return;
        this.informationValueCheckTimer = setInterval(() => {
            void this.reconcileInformationValues();
        }, 400);
    }

    private stopObservingInformationValues(): void {
        if (this.informationValueCheckTimer === undefined) return;
        clearInterval(this.informationValueCheckTimer);
        this.informationValueCheckTimer = undefined;
    }

    private async reconcileInformationValues(): Promise<void> {
        const form = this.shadowRoot?.querySelector<HTMLFormElement>("form.form");
        if (!form) {
            this.stopObservingInformationValues();
            this.observedCalculationValues = undefined;
            return;
        }

        const previous = this.observedCalculationValues;
        const current = this.readCalculationValues(form);
        if (!previous) {
            this.observedCalculationValues = current;
            return;
        }
        if (calculationFieldNames.every((name) => previous[name] === current[name])) {
            this.ensureAutomaticCalculationProgress(form);
            return;
        }

        this.observedCalculationValues = current;
        this.captureEditableInformation(form);
        this.informationRevision += 1;
        this.informationDirty = true;
        this.calculationStale = true;
        this.calculationErrorMessage = "";
        this.submissionErrorMessage = "";
        if (
            (this.registration?.identityFields ?? this.identityValidationFields).some(
                (name) => previous[name] !== current[name],
            )
        ) {
            this.identityAuthenticationRequired = false;
            this.newAccountRequired = false;
        }

        if (previous.countryCode !== current.countryCode) {
            await this.loadAddressRegions(current.countryCode);
        }
        const currentForm = this.shadowRoot?.querySelector<HTMLFormElement>("form.form");
        if (currentForm) this.scheduleAutomaticCalculation(currentForm, 0);
    }

    private rememberCalculationValues(form: HTMLFormElement): void {
        this.observedCalculationValues = this.readCalculationValues(form);
    }

    private readCalculationValues(form: HTMLFormElement): CalculationFieldValues {
        return Object.fromEntries(
            calculationFieldNames.map((name) => {
                const control = form.elements.namedItem(name);
                const value =
                    control instanceof HTMLInputElement || control instanceof HTMLSelectElement
                        ? control.value
                        : this.checkoutCalculationValue(name);
                return [name, value];
            }),
        ) as unknown as CalculationFieldValues;
    }

    private readInitialCalculationValues(form: HTMLFormElement): CalculationFieldValues {
        return Object.fromEntries(
            calculationFieldNames.map((name) => {
                const control = form.elements.namedItem(name);
                if (control instanceof HTMLSelectElement) {
                    // Autofill may run before the first polling snapshot. The selected attribute
                    // preserves the option rendered by Checkout while the live value reflects the
                    // browser's silent selection, allowing that early change to remain observable.
                    const renderedOption = [...control.options].find((option) =>
                        option.hasAttribute("selected"),
                    );
                    return [name, renderedOption?.value ?? control.value];
                }
                return [
                    name,
                    control instanceof HTMLInputElement
                        ? control.value
                        : this.checkoutCalculationValue(name),
                ];
            }),
        ) as unknown as CalculationFieldValues;
    }

    private checkoutCalculationValue(name: CalculationFieldName): string {
        const contact = this.checkout?.contact;
        const address = this.checkout?.shippingAddress;
        switch (name) {
            case "email":
                return contact?.email ?? "";
            case "phone":
                return contact?.phone ?? "";
            case "firstName":
                return address?.firstName ?? "";
            case "lastName":
                return address?.lastName ?? "";
            case "addressLine1":
                return address?.addressLine1 ?? "";
            case "addressLine2":
                return address?.addressLine2 ?? "";
            case "city":
                return address?.city ?? "";
            case "region":
                return address?.region ?? "";
            case "postalCode":
                return address?.postalCode ?? "";
            case "countryCode":
                return address?.countryCode ?? this.commerce?.context.market.countryCode ?? "";
        }
    }

    private async saveInformation(formElement: HTMLFormElement): Promise<{
        commerce: Commerce;
        address: CommerceAddress;
        form: FormData;
    }> {
        const commerce = this.requireCommerce();
        const form = new FormData(formElement);
        await commerce.checkout.updateContact({
            email: String(form.get("email") ?? ""),
            phone: String(form.get("phone") ?? ""),
            acceptsMarketing: form.get("marketing") !== null,
        });
        // Address controls are absent only while an authenticated shopper has
        // deliberately selected a saved address. When the controls are present,
        // their current values always replace the previous checkout address.
        const selectedAddress = form.has("addressLine1") ? null : this.checkout?.shippingAddress;
        const address: CommerceAddress = {
            firstName: String(form.get("firstName") ?? ""),
            lastName: String(form.get("lastName") ?? ""),
            addressLine1: selectedAddress?.addressLine1 ?? String(form.get("addressLine1") ?? ""),
            ...(selectedAddress?.addressLine2
                ? { addressLine2: selectedAddress.addressLine2 }
                : {}),
            city: selectedAddress?.city ?? String(form.get("city") ?? ""),
            region: selectedAddress?.region ?? String(form.get("region") ?? ""),
            postalCode: selectedAddress?.postalCode ?? String(form.get("postalCode") ?? ""),
            countryCode: selectedAddress?.countryCode ?? String(form.get("countryCode") ?? "NZ"),
            ...(selectedAddress?.phone ? { phone: selectedAddress.phone } : {}),
        };
        this.editableAddress = address;
        await commerce.checkout.updateShippingAddress(address);
        return { commerce, address, form };
    }

    private async recalculateInformation(form: HTMLFormElement): Promise<void> {
        if (!this.calculationInputsValid(form)) return;
        if (this.calculationInFlight) {
            this.calculationPending = true;
            return;
        }

        if (this.calculationTimer !== undefined) {
            clearTimeout(this.calculationTimer);
            this.calculationTimer = undefined;
        }
        this.calculationInFlight = true;
        const revision = this.informationRevision;
        this.saving = true;
        this.calculating = true;
        this.calculationErrorMessage = "";
        try {
            const { commerce } = await this.saveInformation(form);
            const calculated = await commerce.checkout.calculate();
            if (revision === this.informationRevision) {
                this.checkout = calculated;
                this.editableContact = undefined;
                this.editableAddress = this.newAddressSelected
                    ? (calculated.shippingAddress ?? undefined)
                    : undefined;
                this.recordCalculation(calculated);
                this.informationDirty = false;
            } else {
                this.calculationStale = true;
                this.calculationPending = true;
            }
        } catch (error) {
            this.calculationStale = true;
            this.calculationErrorMessage = this.describe(error);
        } finally {
            this.calculationInFlight = false;
            this.saving = false;
            this.calculating = false;
            this.releaseCalculationIdleWaiters();
            if (this.calculationPending) {
                this.calculationPending = false;
                const currentForm = this.shadowRoot?.querySelector<HTMLFormElement>("form.form");
                if (currentForm) this.scheduleAutomaticCalculation(currentForm, 0);
            }
        }
    }

    private async prepareExplicitCalculationAction(): Promise<void> {
        this.cancelScheduledCalculation();
        this.informationRevision += 1;
        this.calculationPending = false;
        if (this.calculationInFlight) {
            await new Promise<void>((resolve) => this.calculationIdleWaiters.push(resolve));
        }
        this.cancelScheduledCalculation();
        this.calculationPending = false;
    }

    private releaseCalculationIdleWaiters(): void {
        for (const resolve of this.calculationIdleWaiters.splice(0)) {
            resolve();
        }
    }

    private scheduleAutomaticCalculation(form: HTMLFormElement, delay = 900): void {
        this.cancelScheduledCalculation();
        if (!this.calculationInputsValid(form)) return;
        this.lastAutomaticCalculationRevision = this.informationRevision;
        this.calculationTimer = setTimeout(() => {
            this.calculationTimer = undefined;
            void this.recalculateInformation(form);
        }, delay);
    }

    private ensureAutomaticCalculationProgress(form: HTMLFormElement): void {
        if (
            !this.calculationStale ||
            this.calculationInFlight ||
            this.calculationTimer !== undefined ||
            this.lastAutomaticCalculationRevision === this.informationRevision
        ) {
            return;
        }

        this.scheduleAutomaticCalculation(form, 0);
    }

    private cancelScheduledCalculation(): void {
        if (this.calculationTimer === undefined) return;
        clearTimeout(this.calculationTimer);
        this.calculationTimer = undefined;
    }

    private calculationInputsValid(form: HTMLFormElement): boolean {
        const requiredNames = ["email", "phone", "firstName", "lastName"];
        const hasEditableAddress = form.elements.namedItem("addressLine1") !== null;
        if (hasEditableAddress) {
            requiredNames.push("addressLine1", "city", "region", "postalCode", "countryCode");
        } else if (!this.checkout?.shippingAddress) {
            return false;
        }

        return requiredNames.every((name) => {
            const control = form.elements.namedItem(name);
            return control instanceof HTMLInputElement || control instanceof HTMLSelectElement
                ? control.checkValidity()
                : false;
        });
    }

    private async calculateCurrentCheckoutIfReady(): Promise<void> {
        if (!this.checkout?.contact || !this.checkout.shippingAddress) return;
        this.calculating = true;
        try {
            this.checkout = await this.requireCommerce().checkout.calculate();
            this.recordCalculation(this.checkout);
            this.informationDirty = false;
        } finally {
            this.calculating = false;
        }
    }

    private readonly countryChanged = async (event: Event): Promise<void> => {
        const select = event.currentTarget as HTMLSelectElement;
        this.informationDirty = true;
        this.calculationStale = true;
        this.calculationErrorMessage = "";
        await this.loadAddressRegions(select.value);
        const form = select.form;
        if (form) this.scheduleAutomaticCalculation(form, 0);
    };

    private async loadAddressRegions(countryCode: string): Promise<void> {
        const normalizedCountryCode = countryCode.trim().toUpperCase();
        if (!/^[A-Z]{2}$/u.test(normalizedCountryCode)) {
            this.addressRegions = [];
            this.addressRegionsLoading = false;
            this.addressRegionsErrorMessage = "";
            return;
        }

        this.addressRegionsLoading = true;
        this.addressRegionsErrorMessage = "";
        try {
            this.addressRegions =
                await this.requireCommerce().checkout.getAddressRegions(normalizedCountryCode);
        } catch (error) {
            this.addressRegions = [];
            this.addressRegionsErrorMessage = this.describe(error);
        } finally {
            this.addressRegionsLoading = false;
        }
    }

    private async loadRecurringSavings(): Promise<void> {
        const loadId = ++this.recurringSavingsLoadId;
        const commerce = this.commerce;
        const checkout = this.checkout;
        this.recurringSavings = {};
        if (!commerce || !checkout) return;

        const entries = await Promise.all(
            checkout.items
                .filter((item) => item.source === "cart" && !item.purchaseTypeLocked)
                .map(async (item) => {
                    try {
                        const offers =
                            item.purchaseOptions ??
                            (await commerce.products.getById(item.productId)).variants.find(
                                (candidate) => candidate.id === item.variantId,
                            )?.offers;
                        const oneTime = offers?.find((offer) => offer.purchaseType === "one-time");
                        const recurring = offers?.find(
                            (offer) => offer.purchaseType === "recurring",
                        );
                        if (!oneTime || !recurring || !recurring.frequency) return null;
                        const oneTimeAmount = oneTime.price.current.amount * item.quantity;
                        const recurringAmount = recurring.price.current.amount * item.quantity;
                        const saving = oneTimeAmount - recurringAmount;
                        const formatter = new Intl.NumberFormat(commerce.context.locale.code, {
                            style: "currency",
                            currency: recurring.price.current.currencyCode,
                        });
                        return [
                            item.id,
                            {
                                ...(saving > 0 ? { saving: formatter.format(saving) } : {}),
                                oneTimePrice: formatter.format(oneTimeAmount),
                                recurringPrice: formatter.format(recurringAmount),
                                frequencyOptions: recurring.frequency.options,
                                defaultFrequencyId: recurring.frequency.defaultOptionId,
                                defaultFrequencyDays: recurring.frequency.defaultDays,
                            },
                        ] as const;
                    } catch {
                        return null;
                    }
                }),
        );
        if (loadId !== this.recurringSavingsLoadId || this.checkout !== checkout) return;

        this.recurringSavings = Object.fromEntries(entries.filter((entry) => entry !== null));
    }

    private readonly paymentComplete = (
        event: CustomEvent<{
            result: CommercePaymentResult;
            checkout?: CommerceCheckout;
        }>,
    ): void => {
        void this.handlePaymentComplete(event);
    };

    private async handlePaymentComplete(
        event: CustomEvent<{
            result: CommercePaymentResult;
            checkout?: CommerceCheckout;
        }>,
    ): Promise<void> {
        this.paymentProcessing = false;
        const completedCheckout = event.detail.checkout ?? this.checkout;
        this.completedOrderId = event.detail.result.order.id;
        if (event.detail.result.redirectUrl && typeof window !== "undefined") {
            window.location.assign(event.detail.result.redirectUrl);
            return;
        }
        this.step = "complete";
        this.dispatchEvent(
            new CustomEvent("teqtank-order-submitted", {
                detail: { checkout: completedCheckout },
                bubbles: true,
                composed: true,
            }),
        );
        await this.updateComplete;
        this.scrollConfirmationIntoView();
        if (completedCheckout?.experience !== "enrollment") return;
        try {
            const enrollment = await this.requireCommerce().enrollment.resume();
            const configuredContinuationRoute =
                resolveEnrollmentStageRoute(this.enrollmentStageRoutes, enrollment.currentStage) ||
                this.postCheckoutEnrollmentRoute.trim();
            if (
                enrollment.currentStage.kind !== "checkout" &&
                (enrollment.status === "active" || configuredContinuationRoute.length > 0)
            ) {
                this.enrollmentContinuation = enrollment;
            }
        } catch {
            this.enrollmentContinuation = undefined;
        }
    }

    private scrollConfirmationIntoView(): void {
        if (!this.isConnected || !this.scrollIntoView) return;
        const reducedMotion = globalThis.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
        this.scrollIntoView({
            behavior: reducedMotion ? "auto" : "smooth",
            block: "start",
            inline: "nearest",
        });
    }

    private readonly sponsorChanged = (event: InputEvent): void => {
        this.sponsorWebAlias = (event.currentTarget as HTMLInputElement).value;
        if (this.sponsorSearchTimer !== undefined) clearTimeout(this.sponsorSearchTimer);
        if (this.sponsorWebAlias.trim().length < 3) {
            this.sponsorResults = [];
            return;
        }
        this.sponsorSearchTimer = setTimeout(() => {
            void this.searchSponsors(this.sponsorWebAlias);
        }, 250);
    };

    private async searchSponsors(search: string): Promise<void> {
        try {
            this.sponsorResults = await this.requireCommerce().customer.searchSponsors(search);
        } catch (error) {
            this.submissionErrorMessage = this.describe(error);
        }
    }

    private currentConsentSelections() {
        return (
            this.registration?.consents.map((consent) => ({
                id: consent.id,
                accepted: this.consentSelections[consent.id] === true,
            })) ?? []
        );
    }

    private currentCustomFields(): Readonly<Record<string, string>> {
        return Object.freeze(
            Object.fromEntries(
                (this.registration?.customFields ?? []).map((field) => [
                    field.id,
                    (this.customFieldValues[field.id] ?? "").trim(),
                ]),
            ),
        );
    }

    private async ensureRecaptcha(): Promise<void> {
        const verification = this.registration?.verification;
        if (
            !this.isConnected ||
            verification?.provider !== "recaptcha" ||
            this.recaptchaInitializationFailed ||
            this.commerce?.customer.customer
        )
            return;
        if (!verification.siteKey) {
            this.recaptchaInitializationFailed = true;
            this.humanVerificationError =
                "Human verification is not configured. Contact support before submitting this order.";
            return;
        }
        const container = this.ensureRecaptchaContainer();
        if (
            this.recaptchaWidgetId !== undefined &&
            this.recaptchaContainer === container &&
            container.isConnected
        )
            return;
        if (this.recaptchaWidgetId !== undefined) {
            this.recaptchaWidgetId = undefined;
            this.humanVerificationToken = "";
            container.replaceChildren();
        }
        const recaptcha = await loadRecaptcha();
        if (this.recaptchaWidgetId !== undefined || !container.isConnected) return;
        this.recaptchaContainer = container;
        this.recaptchaWidgetId = recaptcha.render(container, {
            sitekey: verification.siteKey,
            size: this.getBoundingClientRect().width < 410 ? "compact" : "normal",
            callback: (token: string) => {
                this.humanVerificationToken = token;
                this.humanVerificationError = "";
                if (this.submissionErrorMessage.includes("human verification"))
                    this.submissionErrorMessage = "";
            },
            "expired-callback": () => {
                this.humanVerificationToken = "";
                this.humanVerificationError =
                    "Human verification expired. Complete it again before submitting the order.";
            },
            "error-callback": () => {
                this.humanVerificationToken = "";
                this.humanVerificationError =
                    "Human verification could not be completed. Check the secure connection and try again.";
            },
        });
    }

    private ensureRecaptchaContainer(): HTMLElement {
        const existing = Array.from(this.children).find(
            (child): child is HTMLElement =>
                child instanceof HTMLElement && child.dataset.teqtankRecaptchaHost === "true",
        );
        if (existing) return existing;

        const container = document.createElement("div");
        container.slot = "teqtank-recaptcha";
        container.dataset.teqtankRecaptchaHost = "true";
        this.append(container);
        return container;
    }

    private currentHumanVerificationResponse(): string {
        if (this.registration?.verification.provider !== "recaptcha") return "";
        if (this.recaptchaWidgetId === undefined) return "";

        const cachedToken = this.humanVerificationToken.trim();
        const recaptcha = (globalThis as typeof globalThis & { grecaptcha?: RecaptchaApi })
            .grecaptcha;
        if (typeof recaptcha?.getResponse !== "function") return cachedToken;

        try {
            const widgetToken = recaptcha.getResponse(this.recaptchaWidgetId).trim();
            if (!widgetToken) return "";
            if (widgetToken !== cachedToken) this.humanVerificationToken = widgetToken;
            return widgetToken;
        } catch {
            this.recoverRecaptcha(
                "Human verification needs to be completed again. Select “I'm not a robot” and retry.",
            );
            return "";
        }
    }

    private resetIncompleteHumanVerification(): void {
        this.humanVerificationToken = "";
        const recaptcha = (globalThis as typeof globalThis & { grecaptcha?: RecaptchaApi })
            .grecaptcha;
        if (this.recaptchaWidgetId === undefined) return;

        try {
            recaptcha?.reset?.(this.recaptchaWidgetId);
        } catch {
            this.recoverRecaptcha(
                "Human verification needs to be completed again. Select “I'm not a robot” and retry.",
            );
        }
    }

    private consumeHumanVerificationResponse(): void {
        this.humanVerificationToken = "";
        const recaptcha = (globalThis as typeof globalThis & { grecaptcha?: RecaptchaApi })
            .grecaptcha;
        if (this.recaptchaWidgetId !== undefined) {
            try {
                recaptcha?.reset?.(this.recaptchaWidgetId);
            } catch {
                this.recoverRecaptcha();
            }
        }
    }

    private recoverRecaptcha(message?: string): void {
        if (this.recaptchaRecoveryAttempts >= 1) {
            this.recaptchaInitializationFailed = true;
            this.humanVerificationToken = "";
            this.humanVerificationError =
                "Human verification could not be restarted. Refresh Checkout and try again.";
            return;
        }

        this.recaptchaRecoveryAttempts += 1;
        this.releaseRecaptcha();
        if (message) this.humanVerificationError = message;
        void this.ensureRecaptcha().catch(() => {
            this.recaptchaInitializationFailed = true;
            this.humanVerificationError =
                "Human verification could not be restarted. Refresh Checkout and try again.";
        });
    }

    private releaseRecaptcha(): void {
        const widgetId = this.recaptchaWidgetId;
        const container = this.recaptchaContainer;
        const recaptcha = (globalThis as typeof globalThis & { grecaptcha?: RecaptchaApi })
            .grecaptcha;

        this.recaptchaWidgetId = undefined;
        this.recaptchaContainer = undefined;
        this.humanVerificationToken = "";
        this.recaptchaInitializationFailed = false;

        if (widgetId !== undefined) {
            try {
                recaptcha?.reset?.(widgetId);
            } catch {
                // Removing the rendered host is sufficient. A fresh host is created if needed.
            }
        }
        container?.remove();
    }

    private readonly paymentFailed = (event: CustomEvent<{ error: unknown }>): void => {
        this.paymentProcessing = false;
        this.submissionErrorMessage = this.describe(event.detail.error);
    };

    private readonly paymentCancelled = (): void => {
        this.paymentProcessing = false;
    };

    private async runCalculationAction(
        action: () => Promise<void>,
        preserveCalculationOnError = false,
    ): Promise<void> {
        this.saving = true;
        this.calculating = true;
        this.calculationErrorMessage = "";
        this.submissionErrorMessage = "";
        const wasStale = this.calculationStale;
        try {
            await action();
        } catch (error) {
            this.calculationStale = preserveCalculationOnError ? wasStale : true;
            this.calculationErrorMessage = this.describe(error);
        } finally {
            this.saving = false;
            this.calculating = false;
        }
    }

    private async runSubmissionAction(action: () => Promise<void>): Promise<void> {
        this.saving = true;
        this.submissionErrorMessage = "";
        try {
            await action();
        } catch (error) {
            this.submissionErrorMessage = this.describe(error);
        } finally {
            this.saving = false;
        }
    }

    private requireCommerce(): Commerce {
        if (this.commerce === undefined) throw new Error("Commerce runtime is unavailable.");
        return this.commerce;
    }

    private get busy(): boolean {
        return this.saving || this.paymentProcessing;
    }

    private hasAuthoritativeCalculation(checkout: CommerceCheckout): boolean {
        return !this.calculationStale && this.hasCalculationResponse(checkout);
    }

    private hasCalculationResponse(checkout: CommerceCheckout): boolean {
        const invalidCalculationCodes = new Set([
            "calculation-required",
            "calculation-failed",
            "address-invalid",
        ]);
        return (
            checkout.shippingMethods.length > 0 &&
            !checkout.validation.errors.some((error) => invalidCalculationCodes.has(error.code))
        );
    }

    private calculationErrors(checkout: CommerceCheckout): readonly string[] {
        const hasUnknownCalculationFailure =
            !this.calculationStale &&
            !this.calculating &&
            !this.hasCalculationResponse(checkout) &&
            checkout.validation.hasErrors &&
            checkout.validation.errors.length === 0;
        const messages = [
            ...(this.calculationErrorMessage ? [this.calculationErrorMessage] : []),
            ...(hasUnknownCalculationFailure ? [unknownCalculationFailureMessage] : []),
            ...(!this.calculationStale && !this.calculating
                ? checkout.validation.errors.map((error) => error.message)
                : []),
        ];
        return [...new Set(messages)];
    }

    private recordCalculation(checkout: CommerceCheckout): void {
        this.calculationStale = false;
        const calculationCodes = new Set(["calculation-failed", "address-invalid"]);
        this.calculationErrorMessage = checkout.validation.errors
            .filter((error) => calculationCodes.has(error.code))
            .map((error) => error.message)
            .join(" ");
        void this.loadPaymentConfiguration(false);
    }

    private async loadPaymentConfiguration(selectDefault: boolean): Promise<void> {
        const commerce = this.requireCommerce();
        this.paymentConfiguration = await commerce.checkout.getPaymentConfiguration();
        if (
            !selectDefault ||
            this.paymentConfiguration.frameSource ||
            this.paymentConfiguration.selectedSavedPaymentMethodId
        )
            return;
        const methods = commerce.customer.customer?.savedPaymentMethods ?? [];
        const saved = methods.find((method) => method.isDefault) ?? methods[0];
        if (saved) {
            await this.selectPayment(null, saved.id);
            return;
        }
        const option = this.paymentConfiguration.options[0];
        if (option) await this.selectPayment(option.id, null);
    }

    private describe(error: unknown): string {
        return error instanceof Error ? error.message : "Checkout could not be completed.";
    }
}

interface RecaptchaApi {
    ready?(callback: () => void): void;
    getResponse?(widgetId: number): string;
    reset?(widgetId: number): void;
    render(
        container: HTMLElement,
        options: {
            readonly sitekey: string;
            readonly size: "normal" | "compact";
            readonly callback: (token: string) => void;
            readonly "expired-callback": () => void;
            readonly "error-callback": () => void;
        },
    ): number;
}

let recaptchaLoad: Promise<RecaptchaApi> | undefined;

function loadRecaptcha(): Promise<RecaptchaApi> {
    const current = (globalThis as typeof globalThis & { grecaptcha?: RecaptchaApi }).grecaptcha;
    if (current && typeof current.render === "function") return Promise.resolve(current);
    recaptchaLoad ??= new Promise<RecaptchaApi>((resolve, reject) => {
        const existing = document.querySelector<HTMLScriptElement>(
            "script[data-teqtank-recaptcha]",
        );
        const script = existing ?? document.createElement("script");
        const timeout = globalThis.setTimeout(
            () => reject(new Error("Google reCAPTCHA did not initialise within 15 seconds.")),
            15_000,
        );
        const fail = (error: Error): void => {
            globalThis.clearTimeout(timeout);
            reject(error);
        };
        const complete = (): void => {
            const api = (globalThis as typeof globalThis & { grecaptcha?: RecaptchaApi })
                .grecaptcha;
            if (!api) {
                fail(new Error("Google reCAPTCHA did not initialise."));
                return;
            }
            if (typeof api.render === "function") {
                globalThis.clearTimeout(timeout);
                resolve(api);
                return;
            }
            if (typeof api.ready === "function") {
                api.ready(() => {
                    if (typeof api.render === "function") {
                        globalThis.clearTimeout(timeout);
                        resolve(api);
                    } else fail(new Error("Google reCAPTCHA did not initialise."));
                });
                return;
            }
            fail(new Error("Google reCAPTCHA did not initialise."));
        };
        script.addEventListener("load", complete, { once: true });
        script.addEventListener(
            "error",
            () => fail(new Error("Google reCAPTCHA could not be loaded.")),
            { once: true },
        );
        if (!existing) {
            script.src = "https://www.google.com/recaptcha/api.js?render=explicit";
            script.async = true;
            script.defer = true;
            script.dataset.teqtankRecaptcha = "true";
            document.head.append(script);
        } else if (current) {
            complete();
        }
    });
    return recaptchaLoad;
}

declare global {
    interface HTMLElementTagNameMap {
        "teqtank-checkout": TeqtankCheckout;
    }
}
